import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewChild, ElementRef, AfterViewInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import * as fabric from 'fabric';
import { FormElement, FormElementType, FormConfiguration, FormDesignerOptions, PDFPosition, ToolbarOptions, Signer, PASTEL_COLORS, RadioButtonElement, AttachmentsListElement } from '../types/form-element.types';
import { FormElementFactory } from '../factories/form-element.factory';
import { MobileGestureService } from '../services/mobile-gesture.service';
import { CoordinateTransformService } from '../services/coordinate-transform.service';
import { LocalizationService } from '../services/localization.service';
import { Translations } from '../types/localization.types';
import { SizeFloor, NO_FLOOR, clampRect, effectiveFloor, resolveFloor, satisfiesFloor } from '../services/size-constraints';

@Component({
  selector: 'lib-pdf-form-designer',
  imports: [CommonModule, FormsModule, PdfViewerModule],
  templateUrl: './pdf-form-designer.component.html',
  styleUrl: './pdf-form-designer.component.css'
})
export class PdfFormDesignerComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() pdfSrc!: string | Uint8Array;
  @Input() options: FormDesignerOptions = {};
  @Input() initialConfiguration?: FormConfiguration;
  @Input() initialZoom: number = 1.0;
  @Input() initialRotation: number = 0;
  @Input() initialPage: number = 1;
  @Input() externalToolbar: boolean = false;
  @Input() toolbarContainerId: string | null = null;

  pageCount: number = 1;

  @ViewChild('pdfViewer', { static: false }) pdfViewerRef!: any; // PdfViewerComponent
  @ViewChild('pdfContainer', { static: false }) pdfContainerRef!: ElementRef;

  @Output() configurationChange = new EventEmitter<FormConfiguration>();
  @Output() elementSelected = new EventEmitter<FormElement | null>();
  @Output() elementAdded = new EventEmitter<FormElement>();
  @Output() elementRemoved = new EventEmitter<FormElement>();
  @Output() elementModified = new EventEmitter<FormElement>();
  @Output() zoomChange = new EventEmitter<number>();
  @Output() rotationChange = new EventEmitter<number>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() toolbarReady = new EventEmitter<HTMLElement>();

  @ViewChild('toolbarContainer', { static: false }) toolbarContainer!: ElementRef<HTMLDivElement>;

  private canvases: fabric.Canvas[] = [];
  elements: FormElement[] = [];
  selectedElement: FormElement | null = null;
  private isInitialized = false;
  private eventListenersAdded = false;
  private mutationObserver?: MutationObserver;
  private resizeObserver?: ResizeObserver;
  private mobileGestureService = inject(MobileGestureService);
  private coordinateTransform = inject(CoordinateTransformService);
  private localizationService = inject(LocalizationService);
  private dominanceInterval?: number;

  // Modal state for property editing
  isEditModalVisible = false;
  editingElement: FormElement | null = null;
  editForm: any = {};
  editModalError: string | null = null;

  // Color palette for text color picker
  showColorPalette = false;
  colorPalette: string[] = [
    '#000000', '#434343', '#666666', '#999999',
    '#FF0000', '#FF9900', '#FFFF00', '#00FF00',
    '#00FFFF', '#0000FF', '#9900FF', '#FF00FF',
    '#795548', '#800000', '#000080', '#808000'
  ];

  // Distance properties for initials positioning
  distanceFromRight: number = 0;
  distanceFromBottom: number = 0;

  // New properties for click-to-place workflow
  activeElementType: FormElementType | null = null;
  isInPlacementMode = false;
  private crosshairPosition = { x: 0, y: 0 };

  // Signer management properties
  signers: Signer[] = [];
  activeSignerId: string | null = null;
  private usedColorIndices: Set<number> = new Set();
  private nextColorIndex: number = 0;
  readonly MAX_SIGNERS = 50;

  // Pending configuration for late-arriving signers
  private pendingConfiguration: FormConfiguration | null = null;

  /**
   * AN-1851 — the minimum size each element may be resized to, in PDF points, keyed by
   * element id. Fixed once when the element first appears: see `registerSizeFloor`.
   *
   * Held here rather than on the element deliberately. `getConfiguration` builds its export
   * from an explicit field allowlist, so a field on the element would not be serialized
   * today — but keeping the floor off the element entirely means it *cannot* be, even if
   * that export is ever changed to a spread. The floor is a runtime authoring constraint
   * and has no business in stored geometry.
   */
  private sizeFloors: Map<string, SizeFloor> = new Map();

  readonly FormElementType = FormElementType;

  // Will be populated with translated labels in ngOnInit
  availableElements: Array<{ type: FormElementType, label: string, icon: string }> = [];

  // Store translations for template access
  translations: Translations | null = null;

  // Computed toolbar configuration
  get toolbarOptions(): ToolbarOptions {
    return {
      enabled: true,
      external: this.externalToolbar || this.options.toolbar?.external || false,
      containerId: this.toolbarContainerId || this.options.toolbar?.containerId,
      showLabels: this.options.toolbar?.showLabels ?? true,
      showIcons: this.options.toolbar?.showIcons ?? true,
      showSelectedInfo: this.options.toolbar?.showSelectedInfo ?? true,
      showActions: this.options.toolbar?.showActions ?? true,
      customClasses: this.options.toolbar?.customClasses || [],
      customStyles: this.options.toolbar?.customStyles || {},
      ...this.options.toolbar
    };
  }

  get isToolbarExternal(): boolean {
    return this.toolbarOptions.external || false;
  }

  get shouldShowToolbar(): boolean {
    return !this.options.readOnly && (this.toolbarOptions.enabled || false);
  }

  /** Get layout mode from options, defaults to 'boxed' */
  get layoutMode(): 'boxed' | 'full-page' {
    return this.options.layoutMode || 'boxed';
  }

  /** Get toolbar position from options, defaults to 'fixed' */
  get toolbarPositionMode(): 'fixed' | 'sticky' | 'external' {
    return this.options.toolbarPosition || 'fixed';
  }

  /** Get CSS classes for the main container */
  get containerClasses(): string {
    const classes = ['pdf-form-designer'];

    if (this.layoutMode === 'full-page') {
      classes.push('full-page');
    }

    if (this.options.containerClass) {
      classes.push(this.options.containerClass);
    }

    return classes.join(' ');
  }

  /** Get CSS classes for the toolbar */
  get toolbarClasses(): string {
    const classes = ['toolbar'];

    if (this.isToolbarExternal) {
      classes.push('external');
    }

    if (this.toolbarPositionMode === 'sticky') {
      classes.push('sticky');
    }

    return classes.join(' ');
  }

  /**
   * Get filtered available elements based on allowedElements option
   * If allowedElements is specified, only show those element types
   * Otherwise, show all available elements
   */
  get filteredAvailableElements() {
    // AN-1801 — a defined allowedElements list is authoritative, including an
    // empty one: `allowedElements: []` means "no palette" (previously it fell
    // through to show-all). Undefined still means "all available".
    if (this.options.allowedElements) {
      return this.availableElements.filter(element =>
        this.options.allowedElements!.includes(element.type)
      );
    }
    return this.availableElements;
  }

  /**
   * Check if a specific element type is disabled
   * Element buttons are disabled if:
   * 1. Explicitly disabled in options
   * 2. No signers exist
   * 3. No active signer is selected
   */
  isElementDisabled(elementType: FormElementType): boolean {
    // Check if explicitly disabled in options
    if (this.options.disabledElements?.includes(elementType)) {
      return true;
    }

    // Disable all element buttons if no signers exist or no active signer
    return this.signers.length === 0 || !this.activeSignerId;


  }

  ngOnInit(): void {
    this.initializeOptions();

    // Initialize localization
    const locale = this.options.locale || 'en';
    this.localizationService.setLocale(locale);
    this.translations = this.localizationService.getTranslations();

    // Initialize available elements with translations
    this.initializeAvailableElements();

    // Initialize signers from options
    if (this.options.signers) {
      this.signers = [...this.options.signers];
      this.activeSignerId = this.options.activeSignerId || null;

      // Update color tracking for existing signers
      this.signers.forEach(signer => {
        const colorIndex = PASTEL_COLORS.indexOf(signer.color);
        if (colorIndex !== -1) {
          this.usedColorIndices.add(colorIndex);
          this.nextColorIndex = Math.max(this.nextColorIndex, colorIndex + 1);
        }
      });
    }

    // Initialize zoom, rotation, and page from inputs
    this.zoom = this.initialZoom;
    this.rotation = this.initialRotation;
    this.page = this.initialPage;

    if (this.initialConfiguration) {
      this.loadConfiguration(this.initialConfiguration);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle external toolbar configuration changes
    if (changes['options'] || changes['externalToolbar'] || changes['toolbarContainerId']) {
      console.log('External toolbar configuration changed:', {
        external: this.isToolbarExternal,
        containerId: this.toolbarOptions.containerId
      });

      // If external toolbar config changed, re-setup the toolbar
      if (this.isToolbarExternal) {
        setTimeout(() => this.setupExternalToolbar(), 0);
      } else {
        // Restore to internal if external mode was disabled
        this.restoreToolbarToInternal();
      }
    }
  }

  ngAfterViewInit(): void {
    // Ensure container is properly positioned before PDF viewer initializes
    this.ensureContainerPositioning();

    // Handle external toolbar setup with proper timing
    if (this.isToolbarExternal) {
      this.setupExternalToolbar();
    }
  }

  private setupExternalToolbar(): void {
    // Use a timeout to ensure the view is fully initialized
    setTimeout(() => {
      if (this.toolbarContainer?.nativeElement) {
        this.moveToolbarToExternalContainer();
      } else {
        // If toolbar container is not ready, try again after a short delay
        setTimeout(() => this.setupExternalToolbar(), 100);
      }
    }, 0);
  }

  private ensureContainerPositioning(): void {
    // Simple check to ensure container is ready
    setTimeout(() => {
      if (this.pdfContainerRef?.nativeElement) {
        const container = this.pdfContainerRef.nativeElement as HTMLElement;
        // Only ensure the container has absolute positioning - let ng2-pdf-viewer handle the rest
        container.style.position = 'absolute';
      }
    }, 0);
  }


  // ng2-pdf-viewer event handlers
  onPdfLoad(event: any): void {
    console.log('PDF loaded event received:', event);

    // Try multiple ways to get the page count
    if (event && event.pagesCount) {
      this.pageCount = event.pagesCount;
    } else if (event && event._pdfInfo && event._pdfInfo.numPages) {
      this.pageCount = event._pdfInfo.numPages;
    } else if (event && event.numPages) {
      this.pageCount = event.numPages;
    } else {
      // Fallback: try to get from the pdf viewer component
      setTimeout(() => {
        if (this.pdfViewerRef && this.pdfViewerRef.pdf) {
          const numPages = this.pdfViewerRef.pdf.numPages || this.pdfViewerRef.totalPages || 1;
          if (numPages > 1) {
            this.pageCount = numPages;
            console.log(`📄 Updated page count from pdfViewerRef: ${this.pageCount}`);
          }
        }
      }, 500);
    }

    console.log(`📄 PDF loaded with ${this.pageCount} pages`);

    // Setup event listeners once PDF is loaded
    if (!this.isInitialized) {
      setTimeout(() => {
        this.setupPdfPageClickDetection();
        this.isInitialized = true;
      }, 500);
    }
  }

  onPageRendered(event: any): void {
    console.log('Page rendered event received:', event);
    console.log(`📐 Current component zoom: ${this.zoom}, PDF viewer canvas size: ${event?.source?.canvas?.clientWidth || 'unknown'}`);

    const pageNumber = event.pageNumber;
    const pageIndex = pageNumber - 1; // Convert to 0-based index

    // Get PDF canvas directly from the event (avoids issues with multiple canvases)
    const pdfCanvas = event.source?.canvas;
    if (!pdfCanvas) {
      console.warn(`⚠️ Could not get PDF canvas from event for page ${pageNumber}`);
      return;
    }

    console.log(`📐 Got PDF canvas from event for page ${pageNumber}: ${pdfCanvas.clientWidth}x${pdfCanvas.clientHeight}`);

    // Update only elements for this specific page
    if (this.elements.length > 0) {
      const pageElements = this.elements.filter(element => element.pageIndex === pageIndex);
      if (pageElements.length > 0) {
        console.log(`🔄 Page ${pageNumber} rendered with ${pageElements.length} elements - updating canvas`);
        this.updateCanvasForPage(pdfCanvas, event);
      }
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
    this.restoreToolbarToInternal();
  }

  // External toolbar management methods
  moveToolbarToExternalContainer(): void {
    console.log('moveToolbarToExternalContainer called, isToolbarExternal:', this.isToolbarExternal);

    if (!this.isToolbarExternal) return;

    const containerId = this.toolbarOptions.containerId;
    console.log('containerId:', containerId);

    if (!containerId) {
      console.warn('External toolbar enabled but no containerId provided');
      return;
    }

    const externalContainer = document.getElementById(containerId);
    console.log('externalContainer found:', !!externalContainer);

    if (!externalContainer) {
      console.warn(`External toolbar container not found: ${containerId}`);
      return;
    }

    const toolbarElement = this.getToolbarElement();
    console.log('toolbarElement found:', !!toolbarElement);
    console.log('toolbarContainer element:', this.toolbarContainer?.nativeElement);

    if (toolbarElement && toolbarElement.parentNode) {
      // Apply external styles and classes
      this.applyExternalToolbarStyles(toolbarElement);

      // Move toolbar to external container
      externalContainer.appendChild(toolbarElement);
      console.log(`✅ Toolbar successfully moved to external container: ${containerId}`);

      // Emit ready event
      this.toolbarReady.emit(toolbarElement);
    } else {
      console.warn('Toolbar element not found or has no parent node');
    }
  }

  private restoreToolbarToInternal(): void {
    const toolbarElement = this.getToolbarElement();
    if (toolbarElement && toolbarElement.parentNode !== this.toolbarContainer?.nativeElement) {
      // Remove external styles
      this.removeExternalToolbarStyles(toolbarElement);

      // Move back to internal container if it exists
      if (this.toolbarContainer?.nativeElement) {
        this.toolbarContainer.nativeElement.appendChild(toolbarElement);
      }
    }
  }

  getToolbarElement(): HTMLElement | null {
    return this.toolbarContainer?.nativeElement?.querySelector('.toolbar') as HTMLElement || null;
  }

  private applyExternalToolbarStyles(toolbarElement: HTMLElement): void {
    // Remove internal positioning styles
    toolbarElement.classList.add('external');

    // Apply custom classes
    if (this.toolbarOptions.customClasses) {
      toolbarElement.classList.add(...this.toolbarOptions.customClasses);
    }

    // Apply custom styles
    if (this.toolbarOptions.customStyles) {
      Object.assign(toolbarElement.style, this.toolbarOptions.customStyles);
    }
  }

  private removeExternalToolbarStyles(toolbarElement: HTMLElement): void {
    toolbarElement.classList.remove('external');

    // Remove custom classes
    if (this.toolbarOptions.customClasses) {
      toolbarElement.classList.remove(...this.toolbarOptions.customClasses);
    }

    // Clear custom styles
    if (this.toolbarOptions.customStyles) {
      Object.keys(this.toolbarOptions.customStyles).forEach(key => {
        toolbarElement.style.removeProperty(key);
      });
    }
  }

  private initializeOptions(): void {
    this.options = {
      enableGrid: false,
      gridSize: 10,
      enableSnapping: true,
      snapTolerance: 5,
      enableMultiSelect: true,
      enableKeyboardShortcuts: true,
      allowedElements: Object.values(FormElementType) as FormDesignerOptions['allowedElements'],
      readOnly: false,
      ...this.options
    };
  }

  private initializeAvailableElements(): void {
    const t = this.translations;
    if (!t) return;

    this.availableElements = [
      { type: FormElementType.SIGNATURE, label: t.elements.signature, icon: 'ph-bold ph-certificate' },
      { type: FormElementType.BIOMETRIC_SIGNATURE, label: t.elements.biometricSignature, icon: 'ph-bold ph-signature' },
      { type: FormElementType.INITIALS, label: t.elements.initials, icon: 'ph-bold ph-text-aa' },
      { type: FormElementType.TEXT, label: t.elements.text, icon: 'ph-bold ph-text-t' },
      { type: FormElementType.TEXT_INPUT, label: t.elements.textInput, icon: 'ph-bold ph-textbox' },
      { type: FormElementType.RADIO_BUTTON, label: t.elements.radioButton, icon: 'ph-bold ph-radio-button' },
      { type: FormElementType.CHECKBOX, label: t.elements.checkbox, icon: 'ph-bold ph-check-square' },
      { type: FormElementType.DIGITAL_SIGNATURE, label: t.elements.digitalSignature, icon: 'ph-bold ph-key' },
      { type: FormElementType.ATTACHMENTS_LIST, label: t.elements.attachmentsList, icon: 'ph-bold ph-paperclip' },
    ];
  }

  /**
   * Helper method to get translation
   */
  t(key: string, params?: Record<string, any>): string {
    return this.localizationService.translate(key, params);
  }

  /**
   * Get element type label for display
   */
  getElementTypeLabel(type: string): string {
    if (!this.translations) return type;

    const typeMap: Record<string, string> = {
      'text-input': this.translations.elements.textInput,
      'radio-button': this.translations.elements.radioButton,
      'checkbox': this.translations.elements.checkbox,
      'signature': this.translations.elements.signature,
      'digital-signature': this.translations.elements.digitalSignature,
      'biometric-signature': this.translations.elements.biometricSignature,
      'initials': this.translations.elements.initials,
      'text': this.translations.elements.text
    };

    return typeMap[type] || type;
  }



  // Create canvas overlay for a specific PDF canvas
  private async createCanvasForPage(pdfCanvas: HTMLCanvasElement): Promise<void> {
    // Get page index from canvas parent
    const pageWrapper = pdfCanvas.closest('[data-page-number]');
    if (!pageWrapper) {
      console.error('❌ createCanvasForPage: Could not find page wrapper with data-page-number');
      return;
    }

    const pageIndex = parseInt(pageWrapper.getAttribute('data-page-number') || '0') - 1;
    if (pageIndex < 0) {
      console.error('❌ createCanvasForPage: Invalid page number from data-page-number attribute');
      return;
    }

    console.log(`🏗️ createCanvasForPage: Creating overlay for PDF canvas on page ${pageIndex + 1}`);
    await this.createCanvasOverlay(pdfCanvas, pageIndex);
  }

private async createCanvasOverlay(pdfCanvas: HTMLCanvasElement, pageIndex: number): Promise<void> {
    if (!this.pdfContainerRef?.nativeElement) {
      console.error('❌ Main PDF container not found for overlay creation');
      return;
    }

    // Temporarily disconnect the observer to prevent it from triggering during our DOM manipulations
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    console.log(`🎨 Creating fabric canvas on PDF canvas for page ${pageIndex}`);

    // Get CSS dimensions
    const cssW = pdfCanvas.clientWidth;
    const cssH = pdfCanvas.clientHeight;

    try {
      // Create background image from PDF canvas
      const bg = await fabric.FabricImage.fromURL(pdfCanvas.toDataURL('image/png'));
      bg.set({
        selectable: false,
        evented: false
      });
      bg.scaleToWidth(cssW);
      bg.scaleToHeight(cssH);

      // Initialize Fabric on the SAME canvas (will clear it)
      const fabricCanvas = new fabric.Canvas(pdfCanvas, {
        enableRetinaScaling: true,
        selection: this.options.enableMultiSelect !== false,
        preserveObjectStacking: true,
        backgroundImage: bg,
        width: cssW,
        height: cssH,
        allowTouchScrolling: false,
        defaultCursor: 'default',
        moveCursor: 'move',
        hoverCursor: 'move',
        freeDrawingCursor: 'crosshair',
        rotationCursor: 'crosshair',
        interactive: true,
        selectable: true
      });

      // Set background and render
      fabricCanvas.set({
        backgroundImage: bg,
        backgroundColor: '#fff'
      });
      fabricCanvas.requestRenderAll();

      console.log(`✅ Fabric canvas created directly on PDF canvas for page ${pageIndex}`);
      console.log(`📊 Canvas dimensions: ${fabricCanvas.getWidth()}x${fabricCanvas.getHeight()}`);
      console.log(`📊 PDF canvas dimensions: ${pdfCanvas.width}x${pdfCanvas.height}`);
      console.log(`📊 CSS dimensions: ${cssW}x${cssH}`);

      // Ensure the fabric canvas is interactive
      fabricCanvas.wrapperEl.style.pointerEvents = this.options.readOnly ? 'none' : 'all';
      fabricCanvas.wrapperEl.style.zIndex = '10001';

      console.log(`🔧 Fabric canvas wrapper pointer-events: ${fabricCanvas.wrapperEl.style.pointerEvents}`);
      console.log(`🔧 Fabric canvas wrapper z-index: ${fabricCanvas.wrapperEl.style.zIndex}`);

      // Set up events and store canvas
      this.setupCanvasEvents(fabricCanvas, pageIndex);
      this.canvases[pageIndex] = fabricCanvas;

      if (this.options.enableGrid) {
        this.addGridToCanvas(fabricCanvas);
      }

      // Enable mobile gestures if on mobile device
      if (this.isMobileDevice()) {
        this.mobileGestureService.setupMobileGestures(fabricCanvas);
      }

      // Start DOM dominance enforcement if this is the first canvas
      if (pageIndex === 0) {
        this.startDominanceEnforcement();
      }

    } catch (error) {
      console.error(`❌ Error creating fabric canvas with PDF background for page ${pageIndex}:`, error);
    }
  }


  private setupCanvasEvents(canvas: fabric.Canvas, pageIndex: number): void {
    canvas.on('selection:created', (e) => this.handleSelection(e));
    canvas.on('selection:updated', (e) => this.handleSelection(e));
    canvas.on('selection:cleared', () => this.handleSelectionCleared());
    canvas.on('object:modified', (e) => this.handleObjectModified(e, pageIndex));
    // AN-1851 — hold the resize handle at the element's minimum size while it is dragged.
    canvas.on('object:scaling', (e) => this.handleObjectScaling(e, pageIndex));
    canvas.on('mouse:up', (e) => this.handleCanvasMouseUp(e));

    // Add text editing event listeners to synchronize text content changes
    canvas.on('text:changed', (e) => this.handleTextChanged(e, pageIndex));
    canvas.on('text:editing:exited', (e) => this.handleTextEditingExited(e, pageIndex));

    if (this.options.enableSnapping) {
      canvas.on('object:moving', (e) => this.handleObjectSnapping(e, canvas));
    }
  }

  private addGridToCanvas(canvas: fabric.Canvas): void {
    const gridSize = this.options.gridSize || 10;
    const width = canvas.getWidth();
    const height = canvas.getHeight();

    const lines = [];

    for (let i = 0; i <= width / gridSize; i++) {
      lines.push(
        new fabric.Line([i * gridSize, 0, i * gridSize, height], {
          stroke: '#ddd',
          strokeWidth: 0.5,
          selectable: false,
          evented: false,
          opacity: 0.3
        })
      );
    }

    for (let i = 0; i <= height / gridSize; i++) {
      lines.push(
        new fabric.Line([0, i * gridSize, width, i * gridSize], {
          stroke: '#ddd',
          strokeWidth: 0.5,
          selectable: false,
          evented: false,
          opacity: 0.3
        })
      );
    }

    const grid = new fabric.Group(lines, {
      selectable: false,
      evented: false
    });

    canvas.add(grid);
    canvas.sendObjectToBack(grid);
  }

  // Setup PDF page click detection
  private setupPdfPageClickDetection(): void {
    if (!this.pdfContainerRef?.nativeElement) return;

    // Guard against duplicate initialization
    if (this.eventListenersAdded) {
      console.log('🔄 PDF event listeners already added, skipping...');
      return;
    }

    const pdfContainer = this.pdfContainerRef.nativeElement;

    // Add click event listener to the PDF container
    pdfContainer.addEventListener('click', (event: MouseEvent) => {
      this.handlePdfPageClick(event);
    });

    // Add mouse move event for crosshair positioning
    pdfContainer.addEventListener('mousemove', (event: MouseEvent) => {
      this.handleMouseMove(event);
    });

    // Add mouse leave to hide crosshair
    pdfContainer.addEventListener('mouseleave', () => {
      this.hideCrosshair();
    });

    // Note: Scroll events handled by PDF viewer

    // Add escape key handler to cancel placement mode
    document.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape' && this.isInPlacementMode) {
        this.cancelPlacementMode();
      }
    });

    // Mark event listeners as added
    this.eventListenersAdded = true;
    console.log('✅ PDF event listeners added successfully');
  }

  private cancelPlacementMode(): void {
    this.activeElementType = null;
    this.isInPlacementMode = false;
    this.removeCrosshairOverlay();
  }

  /**
   * REMOVED: Legacy coordinate scaling method
   * Now using PDF coordinates that never change with zoom
   */

  /**
   * REMOVED: Legacy coordinate rotation methods
   * Now using PDF coordinates that never change with rotation
   * Screen positions are recalculated from stored PDF coordinates + viewport
   */


  // Find PDF canvas by page number
  // Uses improved selector to specifically target PDF render canvas in .canvasWrapper
  private findCanvasByPageNumber(pageNumber: number): HTMLCanvasElement | null {
    if (!this.pdfContainerRef?.nativeElement) return null;

    const pageWrapper = this.pdfContainerRef.nativeElement.querySelector(`[data-page-number="${pageNumber}"]`);
    if (!pageWrapper) return null;

    // Target PDF render canvas specifically (avoids annotation layer canvases)
    return pageWrapper.querySelector('.canvasWrapper > canvas') as HTMLCanvasElement;
  }

  // Update canvas for a specific page using canvas information
  private async updateCanvasForPage(pdfCanvas: HTMLCanvasElement, pageEvent: any): Promise<void> {
    // Get page index from canvas parent
    const pageWrapper = pdfCanvas.closest('[data-page-number]');
    if (!pageWrapper) {
      console.error('Could not find page wrapper with data-page-number');
      return;
    }
    const pageIndex = parseInt(pageWrapper.getAttribute('data-page-number') || '0') - 1;

    console.log(`🔄 Updating canvas for page ${pageIndex + 1} with event:`, pageEvent);

    const pageElements = this.elements.filter(element => element.pageIndex === pageIndex);
    if (pageElements.length === 0) {
      console.log(`📄 No elements found for page ${pageIndex + 1}, skipping update`);
      return;
    }

    // Dispose existing fabric canvas for this page
    if (this.canvases[pageIndex]) {
      await this.canvases[pageIndex].dispose();
      delete this.canvases[pageIndex];
    }

    // Wait a bit for the PDF canvas to settle after rendering
    setTimeout(async () => {
      // Recreate canvas for this page with new PDF background
      await this.createCanvasOverlay(pdfCanvas, pageIndex);

      // Re-add elements to the new canvas with updated viewport
      const canvas = this.canvases[pageIndex];
      if (canvas) {
        const viewport = this.coordinateTransform.createViewportFromCanvas(pdfCanvas, this.zoom, this.rotation);

        for (const element of pageElements) {
          // Always recalculate screen position from PDF coordinates (never store screen positions)
          if (element.pdfPosition) {
            console.log(`🔄 Recalculating screen position for element ${element.id}: PDF(${element.pdfPosition.x}, ${element.pdfPosition.y})`);

            // Always extract fresh page dimensions from current PDF
            const pageWrapper = pdfCanvas.closest('[data-page-number]');
            if (!pageWrapper) {
              console.error(`❌ Could not find page wrapper for element ${element.id}, skipping`);
              continue;
            }
            const pageDimensions = this.extractPageDimensions(pageWrapper);
            element.pageDimensions = pageDimensions; // Update with current PDF dimensions

            const screenRect = this.coordinateTransform.pdfRectToScreen({
              x: element.pdfPosition.x,
              y: element.pdfPosition.y,
              width: element.pdfPosition.width,
              height: element.pdfPosition.height
            }, viewport, pageDimensions);

            console.log(`📍 Calculated screen position: (${screenRect.x}, ${screenRect.y})`);

            // Update screen position temporarily for fabric object creation
            element.screenPosition = {
              x: screenRect.x,
              y: screenRect.y,
              width: screenRect.width,
              height: screenRect.height,
              angle: element.pdfPosition.angle
            };
          }

          // Get signer information for styling
          const signer = this.getSignerById(element.signerId);
          const fabricObject = FormElementFactory.createFabricObject(element, viewport, undefined, this.translations);

          // Apply signer color as background for signature elements
          if (signer?.color && this.isSignatureElement(element)) {
            this.applySignerColorToSignature(fabricObject, signer.color);
          }

          // Add custom edit/delete controls
          this.addCustomControls(fabricObject, element);

          canvas.add(fabricObject);
          console.log(`✅ Re-added element ${element.type} to updated page ${pageIndex + 1} at screen (${element.screenPosition?.x}, ${element.screenPosition?.y})`);
        }
      }
    }, 50);
  }

  private handleSelection(e: any): void {
    const activeObject = e.selected?.[0] || e.target;
    if (activeObject && (activeObject as any).elementData) {
      this.selectedElement = (activeObject as any).elementData;
      this.elementSelected.emit(this.selectedElement);
    }
  }

  private handleSelectionCleared(): void {
    this.selectedElement = null;
    this.elementSelected.emit(null);
  }

  private handleCanvasMouseUp(e: any): void {
    // Only handle if there's a target object
    if (!e.target) return;

    const fabricObject = e.target;
    const elementData = (fabricObject as any).elementData;

    // Only handle form elements with valid data
    if (!elementData) return;

    const element = this.elements.find(el => el.id === elementData.id);
    if (!element) return;

    // Check if this element belongs to an inactive signer
    const isActiveSignerElement = element.signerId === this.activeSignerId;

    if (!isActiveSignerElement) {
      // This is an inactive element - show confirmation dialog
      this.handleInactiveElementClick(element);
    }
  }

  private handleInactiveElementClick(element: FormElement): void {
    const signer = this.getSignerById(element.signerId);
    if (!signer) return;

    const confirmMessage = this.t('dialogs.switchSignerConfirm', { name: signer.name, email: signer.email });

    if (confirm(confirmMessage)) {
      console.log(`🔄 Switching to signer: ${signer.name} due to element click`);
      void this.activateSigner(signer.id);
    }
  }

  private handleObjectModified(e: any, pageIndex: number): void {
    const fabricObject = e.target;
    if (!fabricObject || !(fabricObject as any).elementData) return;

    const element = (fabricObject as any).elementData as FormElement;
    const boundingRect = fabricObject.getBoundingRect();

    // Get fabric canvas for this page to create viewport
    const canvas = this.canvases[pageIndex];
    if (!canvas) {
      console.error(`Cannot update position: Fabric canvas not found for page ${pageIndex + 1}`);
      return;
    }

    // Create viewport for coordinate transformation using fabric canvas
    const fabricCanvas = canvas.getElement();

    // Extract fresh page dimensions from current PDF
    const pageWrapper = this.pdfContainerRef.nativeElement.querySelector(`[data-page-number="${pageIndex + 1}"]`);
    let pageDimensions: { width: number; height: number };
    if (pageWrapper) {
      pageDimensions = this.extractPageDimensions(pageWrapper);
      element.pageDimensions = pageDimensions; // Update with current PDF dimensions
    } else if (element.pageDimensions) {
      pageDimensions = element.pageDimensions;
      console.warn(`⚠️ Could not find page wrapper for page ${pageIndex + 1}, using stored dimensions`);
    } else {
      console.error(`❌ Element ${element.id} missing pageDimensions and cannot extract from PDF`);
      return;
    }
    const viewport = this.coordinateTransform.createViewportFromCanvas(fabricCanvas, this.zoom, this.rotation);

    // Convert new screen position back to exact PDF coordinates
    // For rotated objects, use the object's center point rather than bounding rect
    const screenRect = {
      x: fabricObject.left || boundingRect.left,
      y: fabricObject.top || boundingRect.top,
      width: boundingRect.width,
      height: boundingRect.height
    };

    const pdfRect = this.coordinateTransform.screenRectToPDF(screenRect, viewport, pageDimensions);

    // Validate that dimensions are positive (not upside down)
    if (pdfRect.width < 0 || pdfRect.height < 0) {
      console.warn('⚠️ Detected invalid (negative) dimensions during modification');
      console.warn(`   Element: ${element.id}, Width: ${pdfRect.width}, Height: ${pdfRect.height}`);

      // Auto-correct by taking absolute values
      pdfRect.width = Math.abs(pdfRect.width);
      pdfRect.height = Math.abs(pdfRect.height);

      console.log('✅ Auto-corrected to positive dimensions');
    }

    // Ensure exact coordinates stay within page bounds
    const boundedRect = {
      x: Math.max(0, Math.min(pageDimensions.width - pdfRect.width, pdfRect.x)),
      y: Math.max(0, Math.min(pageDimensions.height - pdfRect.height, pdfRect.y)),
      width: Math.min(pageDimensions.width, pdfRect.width),
      height: Math.min(pageDimensions.height, pdfRect.height)
    };

    // Capture old dimensions BEFORE updating (for attachments list capacity refresh)
    const oldPdfWidth = element.pdfPosition.width;
    const oldPdfHeight = element.pdfPosition.height;

    // AN-1851 — the authoritative clamp. This is the only guarantee that matters: whatever
    // the live drag did, the geometry written here is at or above the element's floor. It
    // constrains the VALUE only; the transform above is untouched (specification §7).
    const sizeFloor = this.getSizeFloor(element);
    const clampedRect = clampRect(boundedRect, sizeFloor, pageDimensions);
    if (!satisfiesFloor(boundedRect, sizeFloor)) {
      console.log(
        `📐 Clamped ${element.type} ${element.id} to its minimum: ` +
        `${boundedRect.width.toFixed(2)}×${boundedRect.height.toFixed(2)}pt → ` +
        `${clampedRect.width.toFixed(2)}×${clampedRect.height.toFixed(2)}pt`
      );
    }

    // Update exact PDF position (source of truth)
    element.pdfPosition = {
      x: clampedRect.x,
      y: clampedRect.y,
      width: clampedRect.width,
      height: clampedRect.height,
      angle: fabricObject.angle || 0
    };

    element.screenPosition = {
      x: fabricObject.left || boundingRect.left,
      y: fabricObject.top || boundingRect.top,
      width: boundingRect.width,
      height: boundingRect.height,
      angle: fabricObject.angle || 0
    };

    // AN-1851 — if the clamp changed the geometry, the Fabric object on screen still shows
    // the unclamped drag. Reconcile it from the clamped PDF rectangle so what is drawn and
    // what is stored cannot disagree: a box that reads 40pt but looks like 20pt would be a
    // worse defect than the one being fixed. Uses the same transform via the coordinate
    // service; nothing here re-derives it.
    if (!satisfiesFloor(boundedRect, sizeFloor)) {
      const clampedScreen = this.coordinateTransform.pdfRectToScreen(
        {
          x: clampedRect.x,
          y: clampedRect.y,
          width: clampedRect.width,
          height: clampedRect.height
        },
        viewport,
        pageDimensions
      );

      const baseWidth = fabricObject.width || 0;
      const baseHeight = fabricObject.height || 0;

      fabricObject.set({
        left: clampedScreen.x,
        top: clampedScreen.y,
        scaleX: baseWidth > 0 ? clampedScreen.width / baseWidth : fabricObject.scaleX,
        scaleY: baseHeight > 0 ? clampedScreen.height / baseHeight : fabricObject.scaleY
      });
      fabricObject.setCoords();
      canvas.requestRenderAll();

      element.screenPosition = {
        x: clampedScreen.x,
        y: clampedScreen.y,
        width: clampedScreen.width,
        height: clampedScreen.height,
        angle: fabricObject.angle || 0
      };
    }

    // Update legacy position for backward compatibility
    element.position = element.screenPosition;

    // Update properties for text elements
    if (element.type === FormElementType.TEXT) {
      // Update angle property when rotated via handle
      if (fabricObject.angle !== undefined) {
        element.properties.angle = Math.round(360 - fabricObject.angle) % 360;
        console.log(`🔄 Text element rotated to ${element.properties.angle}° (counter-clockwise)`);
      }

      // Update font size when scaled
      if ((fabricObject as any).fontSize !== undefined && fabricObject.scaleY !== undefined) {
        const effectiveFontSize = (fabricObject as any).fontSize * fabricObject.scaleY;
        element.properties.fontSize = Math.round(effectiveFontSize);
        console.log(`🔄 Text element font size updated to ${element.properties.fontSize}px (original: ${(fabricObject as any).fontSize}, scale: ${fabricObject.scaleY})`);
      }

      // Update text content if it has changed (for inline editing via object modification)
      const currentText = (fabricObject as any).text || '';
      if (element.properties.text !== currentText) {
        element.properties.text = currentText;
        console.log(`🔄 Text content synchronized: "${currentText}"`);
      }
    }

    // Special handling for attachments list - recreate to update capacity preview
    // Only do this if the size actually changed (not just moved)
    if (element.type === FormElementType.ATTACHMENTS_LIST) {
      const sizeChanged = Math.abs(oldPdfHeight - boundedRect.height) > 0.1 || Math.abs(oldPdfWidth - boundedRect.width) > 0.1;

      if (sizeChanged) {
        console.log(`🔄 Attachments list size changed: ${oldPdfWidth}x${oldPdfHeight} -> ${boundedRect.width}x${boundedRect.height}`);

        // Remove old object
        canvas.remove(fabricObject);

        // Update screenPosition from new bounding rect
        element.screenPosition = {
          x: fabricObject.left || boundingRect.left,
          y: fabricObject.top || boundingRect.top,
          width: boundingRect.width,
          height: boundingRect.height,
          angle: fabricObject.angle || 0
        };

        // Recreate with new capacity calculation
        const newFabricObject = FormElementFactory.createFabricObject(element, viewport, pageDimensions, this.translations);
        this.addCustomControls(newFabricObject, element);

        canvas.add(newFabricObject);
        canvas.setActiveObject(newFabricObject);
        canvas.renderAll();

        console.log(`🔄 Attachments list recreated with updated capacity preview`);
      }
    }

    console.log(`🔄 Element moved: Screen(${screenRect.x}, ${screenRect.y}) → Exact PDF(${boundedRect.x.toFixed(1)}, ${boundedRect.y.toFixed(1)}) points`);

    this.updateElementInArray(element);
    this.elementModified.emit(element);
    this.emitConfigurationChange();
  }

  /**
   * Handle text content changes in Fabric.js IText objects
   * Updates the underlying element's text property when text is changed inline
   */
  private handleTextChanged(e: any, pageIndex: number): void {
    const fabricObject = e.target;
    if (!fabricObject || !(fabricObject as any).elementData) return;

    const element = (fabricObject as any).elementData as FormElement;

    // Only handle text elements
    if (element.type !== FormElementType.TEXT) return;

    // Update the text property from the Fabric object
    const newText = (fabricObject as any).text || '';
    if (element.properties.text !== newText) {
      element.properties.text = newText;
      console.log(`📝 Text content updated: "${newText}"`);

      // Update element in array and emit changes
      this.updateElementInArray(element);
      this.elementModified.emit(element);
      this.emitConfigurationChange();
    }
  }

  /**
   * Handle when text editing is exited in Fabric.js IText objects
   * Ensures final text state is captured and saved
   */
  private handleTextEditingExited(e: any, pageIndex: number): void {
    const fabricObject = e.target;
    if (!fabricObject || !(fabricObject as any).elementData) return;

    const element = (fabricObject as any).elementData as FormElement;

    // Only handle text elements
    if (element.type !== FormElementType.TEXT) return;

    // Final sync of text content
    const finalText = (fabricObject as any).text || '';
    if (element.properties.text !== finalText) {
      element.properties.text = finalText;
      console.log(`📝 Text editing completed: "${finalText}"`);

      // Update element in array and emit changes
      this.updateElementInArray(element);
      this.elementModified.emit(element);
      this.emitConfigurationChange();
    }
  }

  private handleObjectSnapping(e: any, canvas: fabric.Canvas): void {
    const obj = e.target;
    const snapTolerance = this.options.snapTolerance || 5;
    const gridSize = this.options.gridSize || 10;

    if (this.options.enableGrid) {
      const left = Math.round(obj.left / gridSize) * gridSize;
      const top = Math.round(obj.top / gridSize) * gridSize;

      if (Math.abs(obj.left - left) < snapTolerance) {
        obj.set({ left });
      }

      if (Math.abs(obj.top - top) < snapTolerance) {
        obj.set({ top });
      }
    }
  }

  // New method for selecting form element type (button toggle)
  selectElementType(type: FormElementType): void {
    if (this.options.readOnly) return;

    // Check if the element type is disabled
    if (this.isElementDisabled(type)) {
      console.log(`Element type ${type} is disabled and cannot be selected`);
      return;
    }

    // Toggle the button - if same type is clicked, deselect
    if (this.activeElementType === type) {
      this.activeElementType = null;
      this.isInPlacementMode = false;
      this.removeCrosshairOverlay();
    } else {
      this.activeElementType = type;
      this.isInPlacementMode = true;
      this.setupCrosshairOverlay();
    }
  }

  // Method to place element at specific coordinates on a specific canvas
  async placeElementAt(type: FormElementType, pdfCanvas: HTMLCanvasElement, canvasX: number, canvasY: number): Promise<void> {
    if (this.options.readOnly) return;

    // Check if there's an active signer
    if (!this.activeSignerId) {
      console.warn('Cannot place element: No active signer selected');
      return;
    }

    // Get page index from canvas parent
    const pageWrapper = pdfCanvas.closest('[data-page-number]');
    if (!pageWrapper) {
      console.error('Could not find page wrapper with data-page-number');
      return;
    }
    const pageIndex = parseInt(pageWrapper.getAttribute('data-page-number') || '0') - 1;

    console.log(`🔧 placeElementAt: ${type} on page ${pageIndex + 1} at canvas coordinates (${canvasX}, ${canvasY})`);
    console.log(`📐 Canvas dimensions: ${pdfCanvas.clientWidth}x${pdfCanvas.clientHeight}, zoom: ${this.zoom}`);

    // Extract page dimensions from DOM element
    const pageDimensions = this.extractPageDimensions(pageWrapper);
    console.log(`📏 Page dimensions: ${pageDimensions.width}x${pageDimensions.height} points`);

    // Create viewport information for coordinate transformation
    const viewport = this.coordinateTransform.createViewportFromCanvas(pdfCanvas, this.zoom, this.rotation);
    console.log(`📐 Viewport:`, viewport);

    // Convert canvas coordinates to exact PDF coordinates (immutable)
    const pdfPoint = this.coordinateTransform.screenToPDF(canvasX, canvasY, viewport, pageDimensions);
    console.log(`📍 Canvas (${canvasX}, ${canvasY}) → Exact PDF point:`, pdfPoint);

    // Create exact PDF position with default size
    const elementWidth = this.getDefaultPDFWidth(type, pageDimensions);
    const elementHeight = this.getDefaultPDFHeight(type, pageDimensions);

    const pdfPosition: PDFPosition = {
      x: pdfPoint.x,
      y: pdfPoint.y,
      width: elementWidth,
      height: elementHeight
    };

    // Ensure element stays within page bounds (exact coordinates)
    if (pdfPosition.x + pdfPosition.width > pageDimensions.width) {
      pdfPosition.x = pageDimensions.width - pdfPosition.width;
    }
    if (pdfPosition.y + pdfPosition.height > pageDimensions.height) {
      pdfPosition.y = pageDimensions.height - pdfPosition.height;
    }

    console.log(`📍 Final exact PDF position:`, pdfPosition);

    // Validate exact coordinates (should be within page bounds)
    if (pdfPosition.x < 0 || pdfPosition.y < 0 || pdfPosition.x > pageDimensions.width || pdfPosition.y > pageDimensions.height) {
      console.warn(`⚠️ Invalid exact coordinates: (${pdfPosition.x}, ${pdfPosition.y}) for page ${pageDimensions.width}x${pageDimensions.height}`);
    }

    // Create element with PDF coordinates (immutable storage), page dimensions, and assign to active signer
    // Pass existing elements to generate unique default name with proper suffix
    const element = FormElementFactory.createFormElement(type, pdfPosition, pageIndex, this.activeSignerId || 'default-signer', {}, this.elements);
    element.pageDimensions = pageDimensions;

    // AN-1851 — a newly placed box takes the full type minimum, never a grandfathered one.
    this.registerSizeFloor(element, true);

    // Create canvas for this page if it doesn't exist
    if (!this.canvases[pageIndex]) {
      console.log(`📝 Creating canvas for page ${pageIndex + 1}`);
      await this.createCanvasForPage(pdfCanvas);
    }

    const canvas = this.canvases[pageIndex];
    if (!canvas) {
      console.error(`❌ Canvas creation failed for page ${pageIndex + 1}`);
      return;
    }

    // Create Fabric object with screen coordinates calculated from PDF coordinates
    // Get signer information for styling
    const signer = this.getSignerById(element.signerId);
    const fabricObject = FormElementFactory.createFabricObject(element, viewport, pageDimensions, this.translations);
    console.log(`🎨 Created fabric object at screen position:`, element.screenPosition);

    // Apply signer color as background for signature elements
    if (signer?.color && this.isSignatureElement(element)) {
      this.applySignerColorToSignature(fabricObject, signer.color);
    }

    // Add custom edit/delete controls
    this.addCustomControls(fabricObject, element);

    canvas.add(fabricObject);
    canvas.setActiveObject(fabricObject);

    this.elements.push(element);
    this.selectedElement = element;

    console.log(`✨ Element created and added to canvas. Total elements: ${this.elements.length}`);

    this.elementAdded.emit(element);
    this.elementSelected.emit(element);
    this.emitConfigurationChange();

    // Reset placement mode
    this.activeElementType = null;
    this.isInPlacementMode = false;
    this.removeCrosshairOverlay();
  }

  // Crosshair overlay methods
  private setupCrosshairOverlay(): void {
    // No-op: crosshair is now created dynamically per page
    // This method is kept for compatibility but does nothing
  }

  private createCrosshairElement(): HTMLElement {
    const crosshair = document.createElement('div');
    crosshair.id = 'pdf-crosshair-overlay';
    crosshair.style.position = 'absolute';
    crosshair.style.top = '0';
    crosshair.style.left = '0';
    crosshair.style.right = '0';
    crosshair.style.bottom = '0';
    crosshair.style.pointerEvents = 'none';
    crosshair.style.zIndex = '20000';
    crosshair.style.display = 'block';

    // Create vertical line
    const vLine = document.createElement('div');
    vLine.style.position = 'absolute';
    vLine.style.width = '1px';
    vLine.style.height = '100%';
    vLine.style.backgroundColor = '#ff0000';
    vLine.style.opacity = '0.7';
    vLine.style.top = '0';

    // Create horizontal line
    const hLine = document.createElement('div');
    hLine.style.position = 'absolute';
    hLine.style.height = '1px';
    hLine.style.width = '100%';
    hLine.style.backgroundColor = '#ff0000';
    hLine.style.opacity = '0.7';
    hLine.style.left = '0';

    // Create coordinate display
    const coordDisplay = document.createElement('div');
    coordDisplay.id = 'coordinate-display';
    coordDisplay.style.position = 'absolute';
    coordDisplay.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    coordDisplay.style.color = 'white';
    coordDisplay.style.padding = '4px 8px';
    coordDisplay.style.borderRadius = '4px';
    coordDisplay.style.fontSize = '12px';
    coordDisplay.style.fontFamily = 'monospace';
    coordDisplay.style.zIndex = '20001';

    crosshair.appendChild(vLine);
    crosshair.appendChild(hLine);
    crosshair.appendChild(coordDisplay);

    return crosshair;
  }

  private removeCrosshairOverlay(): void {
    const crosshair = document.getElementById('pdf-crosshair-overlay');
    if (crosshair) {
      crosshair.remove();
    }
  }

  private showCrosshairOnPage(x: number, y: number, pageWrapper: HTMLElement): void {
    let crosshair = document.getElementById('pdf-crosshair-overlay');

    // Create crosshair if it doesn't exist
    if (!crosshair) {
      crosshair = this.createCrosshairElement();
    }

    // Move crosshair to current page if not already there
    if (crosshair.parentElement !== pageWrapper) {
      // Ensure page wrapper has relative positioning for absolute child
      const currentPosition = window.getComputedStyle(pageWrapper).position;
      if (currentPosition === 'static') {
        pageWrapper.style.position = 'relative';
      }
      pageWrapper.appendChild(crosshair);
    }

    crosshair.style.display = 'block';

    const vLine = crosshair.children[0] as HTMLElement;
    const hLine = crosshair.children[1] as HTMLElement;
    const coordDisplay = crosshair.children[2] as HTMLElement;

    vLine.style.left = `${x}px`;
    hLine.style.top = `${y}px`;

    coordDisplay.style.left = `${x + 10}px`;
    coordDisplay.style.top = `${y - 25}px`;
    coordDisplay.textContent = `(${Math.round(x)}, ${Math.round(y)})`;
  }

  private hideCrosshair(): void {
    const crosshair = document.getElementById('pdf-crosshair-overlay');
    if (crosshair) {
      crosshair.style.display = 'none';
    }
  }

  // Event handlers
  private handleMouseMove(event: MouseEvent): void {
    if (!this.isInPlacementMode) return;

    const target = event.target as HTMLElement;

    // Check if hovering over a PDF canvas (not fabric overlay)
    if (target.tagName !== 'CANVAS' || target.id.startsWith('form-designer-canvas-')) {
      this.hideCrosshair();
      return;
    }

    // Find the page wrapper
    const pageWrapper = target.closest('[data-page-number]') as HTMLElement;
    if (!pageWrapper) {
      this.hideCrosshair();
      return;
    }

    // Calculate coordinates relative to canvas
    const canvasRect = target.getBoundingClientRect();
    const x = event.clientX - canvasRect.left;
    const y = event.clientY - canvasRect.top;

    this.crosshairPosition = { x, y };
    this.showCrosshairOnPage(x, y, pageWrapper);
  }

  private async handlePdfPageClick(event: MouseEvent): Promise<void> {
    if (!this.isInPlacementMode || !this.activeElementType) return;

    // Get the clicked element (should be a canvas)
    const clickedElement = event.target as HTMLElement;

    // Verify it's a PDF canvas (not fabric overlay or other element)
    if (clickedElement.tagName !== 'CANVAS' || clickedElement.id.startsWith('form-designer-canvas-')) {
      console.log('Click not on PDF canvas, ignoring');
      return;
    }

    const pdfCanvas = clickedElement as HTMLCanvasElement;

    // Calculate coordinates relative to this specific canvas
    const canvasRect = pdfCanvas.getBoundingClientRect();
    const canvasX = event.clientX - canvasRect.left;
    const canvasY = event.clientY - canvasRect.top;

    // Get page number from parent wrapper's data-page-number
    const pageWrapper = pdfCanvas.closest('[data-page-number]');
    if (!pageWrapper) {
      console.error('Could not find page wrapper with data-page-number');
      return;
    }

    const pageNumber = parseInt(pageWrapper.getAttribute('data-page-number') || '0');
    if (pageNumber === 0) {
      console.error('Invalid page number from data-page-number attribute');
      return;
    }

    const pageIndex = pageNumber - 1; // Convert to 0-based index

    console.log(`Placing ${this.activeElementType} on page ${pageNumber} (index ${pageIndex}) at canvas coordinates (${canvasX}, ${canvasY})`);

    // Prevent event bubbling
    event.stopPropagation();
    event.preventDefault();

    await this.placeElementAt(this.activeElementType, pdfCanvas, canvasX, canvasY);
  }


  removeSelectedElement(): void {
    if (!this.selectedElement || this.options.readOnly) return;
    // AN-1801 — signing placement keeps exactly one non-deletable box.
    if (this.options.signingPlacement) return;

    const canvas = this.canvases[this.selectedElement.pageIndex];
    const activeObject = canvas.getActiveObject();

    if (activeObject) {
      canvas.remove(activeObject);
      this.removeElementFromArray(this.selectedElement);

      this.elementRemoved.emit(this.selectedElement);
      this.selectedElement = null;
      this.elementSelected.emit(null);
      this.emitConfigurationChange();
    }
  }

  /**
   * Validate configuration before loading
   * No backward compatibility - requires v2.0.0 format with signers
   */
  private validateConfiguration(config: FormConfiguration, allowPartial: boolean = false): { valid: boolean; missingSigners?: string[] } {
    // Check version requirement
    if (config.version !== '3.0.0') {
      console.error('❌ Configuration validation failed: Only version 3.0.0 is supported (no backward compatibility)');
      alert(this.t('errors.invalidConfigVersion'));
      return { valid: false };
    }

    // Check signers existence
    if (!config.signers || !Array.isArray(config.signers) || config.signers.length === 0) {
      if (allowPartial && config.elements.length > 0) {
        console.warn('⚠️ Configuration has no signers but has elements - will wait for signers');
        const missingSigners = [...new Set(config.elements.map(el => el.signerId))];
        return { valid: true, missingSigners };
      }
      console.error('❌ Configuration validation failed: No signers found');
      alert(this.t('errors.noSignersInConfig'));
      return { valid: false };
    }

    // Check active signer ID
    if (!config.activeSignerId) {
      console.error('❌ Configuration validation failed: No active signer specified');
      alert(this.t('errors.noActiveSignerInConfig'));
      return { valid: false };
    }

    // Validate active signer exists in signers list (warning only if partial allowed)
    const activeSignerExists = config.signers.some(signer => signer.id === config.activeSignerId);
    if (!activeSignerExists) {
      if (allowPartial) {
        console.warn('⚠️ Active signer not found in current signers list - may arrive later');
      } else {
        console.error('❌ Configuration validation failed: Active signer not found in signers list');
        alert(this.t('errors.activeSignerNotInList'));
        return { valid: false };
      }
    }

    // Validate all elements have signerId
    const elementsWithoutSigner = config.elements.filter(element => !element.signerId);
    if (elementsWithoutSigner.length > 0) {
      console.error('❌ Configuration validation failed: Elements without signer assignments found');
      alert(this.t('errors.elementsWithoutSigner', { count: elementsWithoutSigner.length }));
      return { valid: false };
    }

    // Validate all element signerIds exist in signers list
    const signerIds = new Set(config.signers.map(signer => signer.id));
    const invalidElements = config.elements.filter(element => !signerIds.has(element.signerId));
    if (invalidElements.length > 0) {
      if (allowPartial) {
        const missingSigners = [...new Set(invalidElements.map(el => el.signerId))];
        console.warn(`⚠️ ${invalidElements.length} elements reference signers that don't exist yet. Missing signers: ${missingSigners.join(', ')}`);
        return { valid: true, missingSigners };
      }
      console.error('❌ Configuration validation failed: Elements reference non-existent signers');
      alert(this.t('errors.elementsReferenceInvalidSigners', { count: invalidElements.length }));
      return { valid: false };
    }

    // Validate element dimensions (check for invalid/negative dimensions)
    const invalidDimensionElements = config.elements.filter(element => {
      const pos = element.pdfPosition;
      return !pos || pos.width < 0 || pos.height < 0;
    });

    if (invalidDimensionElements.length > 0) {
      console.warn(`⚠️ Configuration contains ${invalidDimensionElements.length} elements with invalid dimensions (negative width/height)`);
      console.warn('   Invalid element IDs:', invalidDimensionElements.map(e => e.id).join(', '));
      console.warn('   Auto-correcting by taking absolute values...');

      // Auto-correct by taking absolute values
      invalidDimensionElements.forEach(element => {
        if (element.pdfPosition.width < 0) {
          console.warn(`   - Element ${element.id}: width ${element.pdfPosition.width} → ${Math.abs(element.pdfPosition.width)}`);
          element.pdfPosition.width = Math.abs(element.pdfPosition.width);
        }
        if (element.pdfPosition.height < 0) {
          console.warn(`   - Element ${element.id}: height ${element.pdfPosition.height} → ${Math.abs(element.pdfPosition.height)}`);
          element.pdfPosition.height = Math.abs(element.pdfPosition.height);
        }
      });

      console.log(`✅ Auto-corrected ${invalidDimensionElements.length} element(s)`);

      // Show localized warning to user
      alert(this.t('errors.invalidElementDimensions', {
        count: invalidDimensionElements.length,
        ids: invalidDimensionElements.map(e => e.name || e.id).slice(0, 3).join(', ')
      }));
    }

    // Check for zero-sized elements (different issue - should reject)
    const zeroSizedElements = config.elements.filter(element => {
      const pos = element.pdfPosition;
      return pos && (pos.width === 0 || pos.height === 0);
    });

    if (zeroSizedElements.length > 0) {
      console.error(`❌ Configuration validation failed: ${zeroSizedElements.length} elements have zero dimensions`);
      console.error('   Zero-sized element IDs:', zeroSizedElements.map(e => e.id).join(', '));
      alert(this.t('errors.zeroSizedElements', { count: zeroSizedElements.length }));
      return { valid: false };
    }

    console.log('✅ Configuration validation passed');
    return { valid: true };
  }

  async loadConfiguration(config: FormConfiguration, allowPartialLoad: boolean = true): Promise<void> {
    // Validate configuration format (v3.0.0 required - no backward compatibility)
    const validation = this.validateConfiguration(config, allowPartialLoad);
    if (!validation.valid) {
      return; // Validation failed, don't load
    }

    // Update page count from configuration if available
    if (config.pageCount && config.pageCount > 0) {
      this.pageCount = config.pageCount;
      console.log(`📄 Updated page count from configuration: ${this.pageCount}`);
    }

    // If there are missing signers and partial load is allowed, store config and wait
    if (validation.missingSigners && validation.missingSigners.length > 0) {
      console.warn(`⚠️ Configuration has ${validation.missingSigners.length} missing signers. Storing as pending.`);
      console.warn(`   Missing signer IDs: ${validation.missingSigners.join(', ')}`);
      this.pendingConfiguration = config;
      alert(this.t('errors.partialConfigMissingSigners', { signerIds: validation.missingSigners.join('\n') }));
      return;
    }

    this.clearAllElements();

    console.log(`🔄 Loading configuration with ${config.elements.length} elements (version: ${config.version || 'unknown'})`);

    // Load signers - temporarily set first signer as active to ensure all elements load as active
    this.signers = [...config.signers];
    this.activeSignerId = this.signers.length > 0 ? this.signers[0].id : null;

    // Update color tracking for loaded signers
    this.usedColorIndices.clear();
    this.nextColorIndex = 0;
    this.signers.forEach(signer => {
      const colorIndex = PASTEL_COLORS.indexOf(signer.color);
      if (colorIndex !== -1) {
        this.usedColorIndices.add(colorIndex);
        this.nextColorIndex = Math.max(this.nextColorIndex, colorIndex + 1);
      }
    });

    // Normalize imported elements to ensure they have proper PDF positions
    const normalizedElements = config.elements.map(element => this.normalizeImportedElement(element));
    this.elements = normalizedElements;

    // AN-1851 — fix each loaded element's floor from the geometry it arrived with, so a box
    // already stored below the minimum keeps its own height and is never grown by an edit.
    normalizedElements.forEach(element => this.registerSizeFloor(element, false));

    // Cache viewports per page to ensure consistent sizing across all elements
    const pageViewports: Map<number, any> = new Map();

    // Process elements sequentially to avoid race conditions
    for (const element of normalizedElements) {
      const pageNumber = element.pageIndex + 1; // Convert to 1-based for finding canvas

      // Check if fabric canvas already exists for this page
      let pdfCanvas: HTMLCanvasElement | null = null;
      if (!this.canvases[element.pageIndex]) {
        // Only look for PDF canvas if we don't have a fabric canvas yet
        pdfCanvas = this.findCanvasByPageNumber(pageNumber);
        if (!pdfCanvas) {
          console.warn(`⚠️ PDF canvas not found for page ${pageNumber}, element will be added when page renders`);
          continue;
        }

        // Create canvas for this page
        console.log(`📝 Creating canvas for page ${pageNumber} during loadConfiguration`);
        await this.createCanvasForPage(pdfCanvas);
      }

      const canvas = this.canvases[element.pageIndex];
      if (canvas) {
        // Use cached viewport for this page to ensure consistent sizing across all elements
        let viewport = pageViewports.get(element.pageIndex);
        if (!viewport) {
          // Use fabric canvas for viewport if available, otherwise use PDF canvas
          const canvasForViewport = canvas.getElement();
          viewport = this.coordinateTransform.createViewportFromCanvas(canvasForViewport, this.zoom, this.rotation);
          pageViewports.set(element.pageIndex, viewport);
          console.log(`📐 Created viewport for page ${pageNumber}: scale=${viewport.scale}, size=${viewport.width}x${viewport.height}`);
        }

        // Get signer information for debugging
        const signer = this.getSignerById(element.signerId);
        console.log(`📍 Loading element ${element.id} (${element.type}) for signer: ${signer?.name || 'Unknown'} (${element.signerId})`);
        console.log(`📍 PDF position: (${element.pdfPosition?.x}, ${element.pdfPosition?.y}) size: ${element.pdfPosition?.width}x${element.pdfPosition?.height}`);

        // Always extract fresh page dimensions from current PDF
        const pageWrapper = this.pdfContainerRef.nativeElement.querySelector(`[data-page-number="${pageNumber}"]`);
        if (!pageWrapper) {
          console.error(`❌ Could not find page wrapper for page ${pageNumber}, skipping element ${element.id}`);
          continue;
        }
        const pageDimensions = this.extractPageDimensions(pageWrapper);
        element.pageDimensions = pageDimensions; // Update with current PDF dimensions

        if (element.pdfPosition) {
          const screenRect = this.coordinateTransform.pdfRectToScreen({
            x: element.pdfPosition.x,
            y: element.pdfPosition.y,
            width: element.pdfPosition.width,
            height: element.pdfPosition.height
          }, viewport, pageDimensions);

          console.log(`📍 Calculated screen position: (${screenRect.x}, ${screenRect.y}) size: ${screenRect.width}x${screenRect.height}`);

          element.screenPosition = {
            x: screenRect.x,
            y: screenRect.y,
            width: screenRect.width,
            height: screenRect.height,
            angle: element.pdfPosition.angle
          };
        }

        console.log(`🎨 Creating fabric object for signer ${signer?.name} (loading as active)`);

        // Always create elements as active during loading - signer styling will be handled separately
        const fabricObject = FormElementFactory.createFabricObject(element, viewport, pageDimensions, this.translations);

        // Apply signer color as background for signature elements during loading
        if (signer?.color && this.isSignatureElement(element)) {
          this.applySignerColorToSignature(fabricObject, signer.color);
        }

        // Add custom edit/delete controls
        this.addCustomControls(fabricObject, element);

        canvas.add(fabricObject);
        console.log(`✅ Added element ${element.type} to page ${pageNumber} at screen (${element.screenPosition?.x}, ${element.screenPosition?.y})`);
      } else {
        console.error(`❌ Failed to create canvas for page ${pageNumber} during loadConfiguration`);
      }
    }

    // After all elements are loaded, refresh their visual state based on active signer
    this.refreshElementSignerStates();

    this.emitConfigurationChange();
  }

  /**
   * Refresh visual states of all elements based on current active signer
   * Called after loading configuration to show proper active/inactive states
   */
  private refreshElementSignerStates(): void {
    console.log(`🎨 Refreshing element states - Active signer: ${this.activeSignerId}`);

    // Go through all canvases and update element interactivity/styling
    this.canvases.forEach((canvas, pageIndex) => {
      if (!canvas) return;

      const fabricObjects = canvas.getObjects();
      fabricObjects.forEach(fabricObject => {
        const elementData = (fabricObject as any).elementData;
        if (!elementData) return;

        const element = this.elements.find(el => el.id === elementData.id);
        if (!element) return;

        const isActiveSignerElement = element.signerId === this.activeSignerId;
        const signer = this.getSignerById(element.signerId);

        // Apply styling based on active/inactive state
        if (isActiveSignerElement) {
          // Active signer elements: normal styling, fully interactive
          fabricObject.set({
            selectable: true,
            evented: true,
            opacity: 1.0,
            hoverCursor: 'move'
          });

          // Apply signer color as background only to signature elements
          if (signer?.color && this.isSignatureElement(element)) {
            this.applySignerColorToSignature(fabricObject, signer.color);
          }

          // Remove tooltip functionality from active elements
          this.removeSignerTooltip(fabricObject);
        } else {
          // Inactive signer elements: grayed out (25% transparent), limited interactivity
          fabricObject.set({
            selectable: false,
            evented: true, // Keep evented true for click detection
            opacity: 0.75, // 25% transparent
            hoverCursor: 'pointer'
          });

          // Apply signer color as background only to signature elements
          if (signer?.color && this.isSignatureElement(element)) {
            this.applySignerColorToSignature(fabricObject, signer.color);
          }

          // Add tooltip functionality for inactive elements
          if (signer) {
            this.addSignerTooltip(fabricObject, signer);
          }
        }

        console.log(`🎨 Updated ${element.type} (${element.signerId}) - Active: ${isActiveSignerElement}`);
      });

      canvas.renderAll();
    });
  }

  /**
   * Check if an element is a signature-type element
   * @param element - The form element to check
   * @returns True if element is any signature type
   */
  private isSignatureElement(element: FormElement): boolean {
    return [
      FormElementType.SIGNATURE,
      FormElementType.DIGITAL_SIGNATURE,
      FormElementType.BIOMETRIC_SIGNATURE,
      FormElementType.INITIALS
    ].includes(element.type);
  }

  /**
   * Apply signer color as background to signature elements and make text black
   * @param fabricObject - The fabric object to style
   * @param signerColor - The signer's color
   */
  private applySignerColorToSignature(fabricObject: fabric.Object, signerColor: string): void {
    // For group objects (signatures), find the background rectangle and text objects
    if (fabricObject.type === 'group') {
      const group = fabricObject as fabric.Group;
      const objects = group.getObjects();

      // Find the first rect object (background) and set its fill to signer color
      const backgroundRect = objects.find(obj => obj.type === 'rect');
      if (backgroundRect) {
        backgroundRect.set('fill', signerColor);
      }

      // Find all text objects and set their fill to black for better contrast
      const textObjects = objects.filter(obj => obj.type === 'text');
      textObjects.forEach(textObj => {
        textObj.set('fill', '#000000'); // Black text for better readability on colored background
      });
    }
    // For single rect objects, set fill directly
    else if (fabricObject.type === 'rect') {
      fabricObject.set('fill', signerColor);
    }
  }

  /**
   * Add signer tooltip functionality to inactive elements
   * Shows signer name on hover
   */
  private addSignerTooltip(fabricObject: fabric.Object, signer: Signer): void {
    if (!signer) return;

    console.log(`🏷️ Adding tooltip for signer: ${signer.name}`);

    // Remove existing tooltip listeners to avoid duplicates
    fabricObject.off('mouseover');
    fabricObject.off('mouseout');

    // Add mouse over event to show tooltip
    fabricObject.on('mouseover', (e) => {
      this.showSignerTooltip(e, signer);
    });

    // Add mouse out event to hide tooltip
    fabricObject.on('mouseout', () => {
      this.hideSignerTooltip();
    });
  }

  /**
   * Remove signer tooltip functionality from active elements
   * @param fabricObject - The fabric object to remove tooltip from
   */
  private removeSignerTooltip(fabricObject: fabric.Object): void {
    console.log(`🏷️ Removing tooltip from active element`);

    // Remove tooltip event listeners
    fabricObject.off('mouseover');
    fabricObject.off('mouseout');

    // Hide any currently visible tooltip
    this.hideSignerTooltip();
  }

  /**
   * Show signer tooltip at mouse position
   */
  private showSignerTooltip(e: any, signer: Signer): void {
    // Create tooltip element if it doesn't exist
    let tooltip = document.getElementById('signer-tooltip');
    if (!tooltip) {
      tooltip = document.createElement('div');
      tooltip.id = 'signer-tooltip';
      tooltip.style.cssText = `
        position: fixed !important;
        background: rgba(0, 0, 0, 0.9) !important;
        color: white !important;
        padding: 8px 12px !important;
        border-radius: 4px !important;
        font-size: 12px !important;
        font-family: Arial, sans-serif !important;
        z-index: 999999 !important;
        pointer-events: none !important;
        white-space: nowrap !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
        border-left: 3px solid ${signer.color} !important;
        opacity: 1 !important;
        visibility: visible !important;
        display: block !important;
        min-width: 100px !important;
        max-width: 300px !important;
      `;
      document.body.appendChild(tooltip);
    }

    // Update tooltip content and position
    tooltip.textContent = `${signer.name} (${signer.email})`;
    tooltip.style.borderLeftColor = signer.color;
    tooltip.style.display = 'block';
    tooltip.style.visibility = 'visible';
    tooltip.style.opacity = '1';

    // Position tooltip near mouse cursor
    if (e.e) {
      const mouseEvent = e.e;
      let left = mouseEvent.clientX + 10;
      let top = mouseEvent.clientY - 30;

      // Ensure tooltip stays within viewport bounds
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Adjust horizontal position if tooltip would go off-screen
      if (left + 200 > viewportWidth) {
        left = mouseEvent.clientX - 210;
      }

      // Adjust vertical position if tooltip would go off-screen
      if (top < 0) {
        top = mouseEvent.clientY + 20;
      }
      if (top + 50 > viewportHeight) {
        top = mouseEvent.clientY - 50;
      }

      tooltip.style.left = left + 'px';
      tooltip.style.top = top + 'px';
    }
  }

  /**
   * Hide signer tooltip
   */
  private hideSignerTooltip(): void {
    const tooltip = document.getElementById('signer-tooltip');
    if (tooltip) {
      tooltip.style.display = 'none';
    }
  }

  /**
   * Normalize imported element to ensure it has proper PDF position
   */
  private normalizeImportedElement(element: any): FormElement {
    // Ensure the element has a PDF position
    let pdfPosition: PDFPosition;

    if (element.pdfPosition) {
      // New format - already has PDF position
      pdfPosition = element.pdfPosition;
      console.log(`📄 Element ${element.id} has pdfPosition: (${pdfPosition.x}, ${pdfPosition.y})`);
    } else if (element.position) {
      // Legacy format - position field contains coordinates
      pdfPosition = {
        x: element.position.x || 0,
        y: element.position.y || 0,
        width: element.position.width || 100,
        height: element.position.height || 20,
        angle: element.position.angle || 0
      };
      console.log(`📄 Element ${element.id} converted from legacy position: (${pdfPosition.x}, ${pdfPosition.y})`);
    } else {
      // Fallback - create default position
      pdfPosition = {
        x: 50,
        y: 50,
        width: 100,
        height: 20,
        angle: 0
      };
      console.warn(`⚠️ Element ${element.id} missing position data, using defaults`);
    }

    return {
      ...element,
      pdfPosition,
      // Clear screen position and page dimensions - they will be recalculated from current PDF
      screenPosition: undefined,
      pageDimensions: undefined
    } as FormElement;
  }

  getConfiguration(): FormConfiguration {
    return {
      version: '3.0.0',
      signers: [...this.signers],
      activeSignerId: this.activeSignerId || (this.signers.length > 0 ? this.signers[0].id : ''),
      elements: this.elements.map(element => ({
        id: element.id,
        type: element.type,
        name: element.name || '', // Include name property
        pageIndex: element.pageIndex,
        signerId: element.signerId,
        isReadOnly: element.isReadOnly || false, // Include isReadOnly property

        // Export using the new format with exact coordinates and page dimensions
        pdfPosition: element.pdfPosition || this.convertLegacyPosition(element.position),
        pageDimensions: element.pageDimensions || (() => {
          console.warn(`⚠️ Element ${element.id} missing pageDimensions during export - this indicates a data integrity issue`);
          return { width: 612, height: 792 };
        })(), // Ensure page dimensions are exported
        position: element.pdfPosition || element.position || { x: 0, y: 0, width: 100, height: 20 },

        properties: element.properties,
        styles: element.styles
      })) as FormElement[],
      pageCount: this.pageCount,
      metadata: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        coordinateSystem: 'PDF_EXACT_POINTS' as const
      }
    };
  }

  /**
   * Convert legacy screen positions to PDF positions if needed
   */
  private convertLegacyPosition(position: any): PDFPosition | null {
    if (!position || typeof position !== 'object') return null;

    // If it looks like a legacy screen position, we can't convert it accurately
    // without knowing the original canvas size, so return as-is and hope for the best
    return {
      x: position.x || 0,
      y: position.y || 0,
      width: position.width || 100,
      height: position.height || 20,
      angle: position.angle || 0
    };
  }

  clearAllElements(): void {
    this.canvases.forEach(canvas => {
      canvas.getObjects().forEach(obj => {
        if ((obj as any).elementData) {
          canvas.remove(obj);
        }
      });
    });

    this.elements = [];
    this.selectedElement = null;
    this.sizeFloors.clear(); // AN-1851 — floors are per-element and do not outlive them
    this.elementSelected.emit(null);
    this.emitConfigurationChange();
  }

  private updateElementInArray(element: FormElement): void {
    const index = this.elements.findIndex(e => e.id === element.id);
    if (index !== -1) {
      this.elements[index] = element;
    }
  }

  private removeElementFromArray(element: FormElement): void {
    const index = this.elements.findIndex(e => e.id === element.id);
    if (index !== -1) {
      this.elements.splice(index, 1);
    }
  }

  /**
   * Remove element by ID (used in signer cascade delete)
   * @param elementId - ID of the element to remove
   */
  private removeElementById(elementId: string): void {
    const element = this.elements.find(e => e.id === elementId);
    if (!element) return;

    // Remove from canvas
    const canvas = this.canvases[element.pageIndex];
    if (canvas) {
      const fabricObjects = canvas.getObjects();
      const fabricObject = fabricObjects.find(obj => (obj as any).elementData?.id === elementId);
      if (fabricObject) {
        canvas.remove(fabricObject);
      }
    }

    // Remove from elements array
    this.removeElementFromArray(element);

    // Update selection if this was the selected element
    if (this.selectedElement?.id === elementId) {
      this.selectedElement = null;
      this.elementSelected.emit(null);
    }

    // Emit events
    this.elementRemoved.emit(element);
    this.emitConfigurationChange();
  }

  private emitConfigurationChange(): void {
    this.configurationChange.emit(this.getConfiguration());
  }

  /**
   * Add custom edit/delete controls to fabric objects
   */
  private addCustomControls(fabricObject: fabric.Object, element?: FormElement): void {
    if (this.options.readOnly) return;

    // Check if element is a signature type (exclude edit icon for these)
    const isSignatureType = element && (
      element.type === FormElementType.SIGNATURE ||
      element.type === FormElementType.BIOMETRIC_SIGNATURE ||
      element.type === FormElementType.DIGITAL_SIGNATURE
    );

    // Define custom controls for edit and delete
    const editIcon = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='%23007bff'%3E%3Cpath d='M14.06 9.02l.92.92L5.92 19H5v-.92l9.06-9.06M17.66 3c-.25 0-.51.1-.7.29l-1.83 1.83 3.75 3.75 1.83-1.83c.39-.39.39-1.02 0-1.41L17.66 3.29c-.18-.18-.44-.29-.7-.29zm-3.6 3.19L3 17.25V21h3.75L17.81 9.94l-3.75-3.75z'/%3E%3C/svg%3E";
    const deleteIcon = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='%23dc3545'%3E%3Cpath d='M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z'/%3E%3C/svg%3E";

    // Only add edit control for non-signature types
    if (!isSignatureType) {
      fabricObject.controls['edit'] = new fabric.Control({
        x: 0.5,
        y: 0.5,
        offsetY: 10,
        offsetX: -16,
        cursorStyle: 'pointer',
        sizeX: 22,
        sizeY: 20,
        touchSizeX: 22,
        touchSizeY: 20,
        mouseUpHandler: (eventData, transformData, x, y) => {
          const element = this.elements.find(el => el.id === (fabricObject as any).id);
          if (element) {
            this.openEditModal(element);
          }
          return true;
        },
        render: (ctx, left, top, styleOverride, fabricObject) => {
          const width = 22;
          const height = 20;
          const radius = 4;
          const iconSize = 14;

          // Draw background rounded rectangle with border
          ctx.save();
          ctx.fillStyle = '#ffffff';
          ctx.strokeStyle = '#007bff';
          ctx.lineWidth = 1;

          // Draw rounded rectangle
          ctx.beginPath();
          ctx.moveTo(left - width/2 + radius, top - height/2);
          ctx.lineTo(left + width/2 - radius, top - height/2);
          ctx.quadraticCurveTo(left + width/2, top - height/2, left + width/2, top - height/2 + radius);
          ctx.lineTo(left + width/2, top + height/2 - radius);
          ctx.quadraticCurveTo(left + width/2, top + height/2, left + width/2 - radius, top + height/2);
          ctx.lineTo(left - width/2 + radius, top + height/2);
          ctx.quadraticCurveTo(left - width/2, top + height/2, left - width/2, top + height/2 - radius);
          ctx.lineTo(left - width/2, top - height/2 + radius);
          ctx.quadraticCurveTo(left - width/2, top - height/2, left - width/2 + radius, top - height/2);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();

          // Draw icon
          const img = new Image();
          img.onload = () => {
            ctx.drawImage(img, left - iconSize/2, top - iconSize/2, iconSize, iconSize);
          };
          img.src = editIcon;
          ctx.restore();
        }
      });
    }

    // Add delete control - adjust position based on whether edit icon exists
    fabricObject.controls['delete'] = new fabric.Control({
      x: 0.5,
      y: 0.5,
      offsetY: 10,
      offsetX: isSignatureType ? -10 : 10, // Center if no edit icon, otherwise to the right
      cursorStyle: 'pointer',
      sizeX: 22,
      sizeY: 20,
      touchSizeX: 22,
      touchSizeY: 20,
      mouseUpHandler: (eventData, transformData, x, y) => {
        const element = this.elements.find(el => el.id === (fabricObject as any).id);
        if (element) {
          this.deleteElement(element);
        }
        return true;
      },
      render: (ctx, left, top, styleOverride, fabricObject) => {
        const width = 22;
        const height = 20;
        const radius = 4;
        const iconSize = 14;

        // Draw background rounded rectangle with border
        ctx.save();
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = '#dc3545';
        ctx.lineWidth = 1;

        // Draw rounded rectangle
        ctx.beginPath();
        ctx.moveTo(left - width/2 + radius, top - height/2);
        ctx.lineTo(left + width/2 - radius, top - height/2);
        ctx.quadraticCurveTo(left + width/2, top - height/2, left + width/2, top - height/2 + radius);
        ctx.lineTo(left + width/2, top + height/2 - radius);
        ctx.quadraticCurveTo(left + width/2, top + height/2, left + width/2 - radius, top + height/2);
        ctx.lineTo(left - width/2 + radius, top + height/2);
        ctx.quadraticCurveTo(left - width/2, top + height/2, left - width/2, top + height/2 - radius);
        ctx.lineTo(left - width/2, top - height/2 + radius);
        ctx.quadraticCurveTo(left - width/2, top - height/2, left - width/2 + radius, top - height/2);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // Draw icon
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, left - iconSize/2, top - iconSize/2, iconSize, iconSize);
        };
        img.src = deleteIcon;
        ctx.restore();
      }
    });

    // Configure resize controls using built-in Fabric.js API
    // Hide unwanted corner controls by deleting them
    delete fabricObject.controls['tl']; // top-left
    delete fabricObject.controls['bl']; // bottom-left
    delete fabricObject.controls['br']; // bottom-right

    // Hide unwanted edge controls
    delete fabricObject.controls['ml']; // middle-left
    delete fabricObject.controls['mb']; // middle-bottom

    // Configure resize controls based on element type
    if (element && element.type === FormElementType.TEXT) {
      // For text elements: only proportional resize + rotation
      delete fabricObject.controls['mt']; // Remove height resize
      delete fabricObject.controls['mr']; // Remove width resize
      // Keep tr (top-right) - proportional resize
      // Keep mtr (rotation) - controls angle property
    } else {
      // For other elements: keep 3 resize controls, remove rotation
      // Keep only the 3 desired resize controls:
      // mt (mid-top) - height resize only
      // mr (mid-right) - width resize only
      // tr (top-right) - proportional resize
      delete fabricObject.controls['mtr']; // Remove rotation handle
    }

    // Ensure controls are visible with proper styling
    fabricObject.hasControls = true;
    fabricObject.hasBorders = true;
    fabricObject.cornerStyle = 'rect';
    fabricObject.cornerSize = 8;
    fabricObject.transparentCorners = false;
    fabricObject.cornerColor = '#007bff';
    fabricObject.cornerStrokeColor = '#ffffff';
  }

  // ---------------------------------------------------------------------------------------
  // AN-1851 — resize clamp for signature stamp boxes
  //
  // The server sizes every element of the stamp from one root value (root = boxHeight / 7)
  // with no floor and no alternative layout, so a box shorter than the minimum renders the same
  // four-row stamp with proportionally smaller type until the identifier is unreadable. The
  // clamp stops NEW boxes there. It cannot reach geometry already stored, and must not try:
  // see `registerSizeFloor`.
  //
  // NOTE FOR ANYONE EDITING THIS SECTION: the pixel floor is obtained by CALLING
  // `coordinateTransform.pdfRectToScreen`, never by re-deriving the scale here. That
  // function has a second hand-maintained copy in the signing page of ilerian/agree
  // (`signing.component.ts`'s `pdfToScreen`) which positions overlays on in-progress
  // documents. A local re-derivation would be a third copy of the same formula. Do not add
  // one, and do not change the transform — `coordinate-transform.service.spec.ts` will fail
  // and tell you why. See AN-1851 specification §7.
  // ---------------------------------------------------------------------------------------

  /**
   * Fix an element's minimum size, once, at the moment it first appears.
   *
   * `isNewPlacement` distinguishes a box the user has just dropped from one loaded out of a
   * saved configuration. It matters because `handleObjectModified` fires on **move** as well
   * as resize and writes `pdfPosition` unconditionally: a flat 40pt floor would mean that
   * opening a template containing a 35pt box and nudging it one point sideways silently grew
   * it to 40pt and saved that. A loaded box below the minimum therefore keeps its own height
   * as its floor — never grown, and still not shrinkable any further.
   */
  private registerSizeFloor(element: FormElement, isNewPlacement: boolean): void {
    this.sizeFloors.set(element.id, resolveFloor({
      type: element.type,
      loadedHeight: isNewPlacement ? undefined : element.pdfPosition?.height,
      loadedWidth: isNewPlacement ? undefined : element.pdfPosition?.width,
      signingPlacement: this.options.signingPlacement,
      overrides: {
        minStampHeight: this.options.minStampHeight,
        minStampWidthEm: this.options.minStampWidthEm
      }
    }));
  }

  /** The registered floor, or no constraint for an element that was never registered. */
  private getSizeFloor(element: FormElement): SizeFloor {
    return this.sizeFloors.get(element.id) ?? NO_FLOOR;
  }

  /**
   * The element's floor expressed in screen pixels, for clamping a live drag.
   *
   * Derived by asking the coordinate service to transform a probe rectangle, so this stays
   * correct at any zoom without duplicating the transform. Returns null when the element is
   * unconstrained or the viewport cannot be established, in which case the caller leaves the
   * drag alone and the authoritative clamp in `handleObjectModified` still applies.
   */
  private getScreenFloor(element: FormElement, pageIndex: number): { width: number; height: number } | null {
    const floor = this.getSizeFloor(element);
    if (floor.widthEm === 0 && floor.height === 0) return null;

    const canvas = this.canvases[pageIndex];
    if (!canvas) return null;

    const pageWrapper = this.pdfContainerRef?.nativeElement
      ?.querySelector(`[data-page-number="${pageIndex + 1}"]`);
    const pageDimensions = pageWrapper
      ? this.extractPageDimensions(pageWrapper)
      : element.pageDimensions;
    if (!pageDimensions) return null;

    const viewport = this.coordinateTransform.createViewportFromCanvas(
      canvas.getElement(), this.zoom, this.rotation
    );

    // Resolve the em-based width before transforming, because the required width is a function
    // of box height and there is no single pixel width to cache.
    //
    // Note the zero height: `effectiveFloor` answers "what is the smallest this RECTANGLE may
    // be", so passing the element's live geometry returns max(currentHeight, floor) — which for
    // any box already taller than the minimum hands back its own current height as the floor,
    // and the live drag then refuses to shrink it at all. Passing zero asks the question this
    // caller actually has: what is the smallest the box may EVER be. Caught by the
    // handleObjectScaling specs.
    const effective = effectiveFloor({ ...element.pdfPosition, height: 0 }, floor);

    const probe = this.coordinateTransform.pdfRectToScreen(
      { x: 0, y: 0, width: effective.width, height: effective.height },
      viewport,
      pageDimensions
    );

    return { width: probe.width, height: probe.height };
  }

  /**
   * Hold the resize handle at the element's floor while it is being dragged.
   *
   * This is what makes the handle visibly stop, which is the behaviour specified — the type
   * is never shrunk to fit. Correctness does not depend on it: `handleObjectModified` clamps
   * the value that actually gets stored.
   *
   * Signature elements are Fabric Groups, so a resize sets `scaleX`/`scaleY` rather than
   * width and height. The floor is therefore applied to the scale factors, computed against
   * the object's unscaled size.
   */
  private handleObjectScaling(e: any, pageIndex: number): void {
    const fabricObject = e?.target;
    if (!fabricObject || !(fabricObject as any).elementData) return;

    const element = (fabricObject as any).elementData as FormElement;
    const screenFloor = this.getScreenFloor(element, pageIndex);
    if (!screenFloor) return;

    const baseWidth = fabricObject.width || 0;
    const baseHeight = fabricObject.height || 0;

    // Which handle is being dragged. Fabric names it on the live transform; if it is absent we
    // fall back to correcting nothing, which is the pre-existing behaviour.
    const corner: string = e?.transform?.corner || '';

    if (screenFloor.height > 0 && baseHeight > 0) {
      const minScaleY = screenFloor.height / baseHeight;

      if ((fabricObject.scaleY || 1) < minScaleY) {
        // The edge the drag is NOT moving has to stay where it is.
        //
        // Fabric leaves originY at its default of 'top', so an object grows downward from its
        // top edge. Dragging the middle-top handle lowers `top` and shrinks `scaleY` together;
        // restoring only `scaleY` re-grows the box downward from the lowered top, so the box
        // walks down the page for as long as the drag continues. Reported from a real document,
        // and the reason this method now has tests.
        //
        // For the handles that move the TOP edge — mt, and tr which moves the top-right corner
        // — pin the bottom edge: read where it is now, then put `top` back so the clamped
        // height reaches exactly that line. mr moves the right edge only and needs nothing.
        if (corner === 'mt' || corner === 'tr') {
          const bottomEdge = (fabricObject.top || 0) + baseHeight * (fabricObject.scaleY || 1);
          fabricObject.set({
            scaleY: minScaleY,
            top: bottomEdge - baseHeight * minScaleY
          });
        } else {
          fabricObject.set({ scaleY: minScaleY });
        }
      }
    }

    if (screenFloor.width > 0 && baseWidth > 0) {
      const minScaleX = screenFloor.width / baseWidth;
      if ((fabricObject.scaleX || 1) < minScaleX) {
        // No origin correction here: every handle that resizes width (mr, tr) moves the RIGHT
        // edge, so the left edge is already the anchor and originX defaults to 'left'.
        fabricObject.set({ scaleX: minScaleX });
      }
    }

    fabricObject.setCoords();
  }

  /**
   * Get default width in PDF points for form elements
   * These are FIXED sizes that work consistently across all page sizes
   */
  private getDefaultPDFWidth(type: FormElementType, _pageDimensions: { width: number; height: number }): number {
    switch (type) {
      case FormElementType.TEXT_INPUT: return 144;           // 2 inches
      case FormElementType.RADIO_BUTTON: return 14;          // Square (same as height)
      case FormElementType.CHECKBOX: return 14;              // Square (same as height)
      case FormElementType.SIGNATURE: return 144;            // 2 inches
      case FormElementType.DIGITAL_SIGNATURE: return 144;    // 2 inches
      case FormElementType.BIOMETRIC_SIGNATURE: return 144;  // 2 inches
      case FormElementType.INITIALS: return 60;              // ~0.83 inches
      case FormElementType.TEXT: return 72;                  // 1 inch
      case FormElementType.ATTACHMENTS_LIST: return 288;     // 4 inches
      default: return 72;
    }
  }

  /**
   * Get default height in PDF points for form elements
   * These are FIXED sizes that work consistently across all page sizes
   */
  private getDefaultPDFHeight(type: FormElementType, _pageDimensions: { width: number; height: number }): number {
    switch (type) {
      case FormElementType.TEXT_INPUT: return 24;            // Standard form field height
      case FormElementType.RADIO_BUTTON: return 14;          // Square
      case FormElementType.CHECKBOX: return 14;              // Square
      case FormElementType.SIGNATURE: return 60;             // ~0.83 inches
      case FormElementType.DIGITAL_SIGNATURE: return 60;     // ~0.83 inches
      case FormElementType.BIOMETRIC_SIGNATURE: return 60;   // ~0.83 inches
      case FormElementType.INITIALS: return 37;              // ~0.5 inches
      case FormElementType.TEXT: return 18;                  // Standard text height
      case FormElementType.ATTACHMENTS_LIST: return 108;     // 1.5 inches
      default: return 14;
    }
  }

  /**
   * Extract PDF page dimensions from DOM element's style attribute
   * Example style: "width: round(var(--scale-factor) * 612px, 1px); height: round(var(--scale-factor) * 792px, 1px);"
   */
  private extractPageDimensions(pageWrapper: Element): { width: number; height: number } {
    const style = pageWrapper.getAttribute('style') || '';

    // Extract width and height from style string
    // Handle multiple CSS formats:
    // - Old: width: round(var(--scale-factor) * 297.6378px)
    // - New: width: round(down, var(--scale-factor) * 297.6378px, var(--scale-round-x, 1px))
    // Just look for the pattern: var(--scale-factor) * <number>px after width:/height:
    const widthMatch = style.match(/width:[^;]*var\(--scale-factor\)\s*\*\s*(\d+(?:\.\d+)?)px/);
    const heightMatch = style.match(/height:[^;]*var\(--scale-factor\)\s*\*\s*(\d+(?:\.\d+)?)px/);

    if (widthMatch && heightMatch) {
      const width = parseFloat(widthMatch[1]);
      const height = parseFloat(heightMatch[1]);
      console.log(`📐 Extracted page dimensions: ${width}x${height} points`);
      return { width, height };
    }

    // Fallback - this should rarely happen, log error for debugging
    console.error('❌ CRITICAL: Could not extract page dimensions from style attribute. This may cause incorrect element positioning.');
    console.error('   Style attribute:', style);
    console.error('   Expected format: width: round(...var(--scale-factor) * <number>px...)');
    return { width: 612, height: 792 };
  }

  private isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (navigator.maxTouchPoints > 0 && window.screen.width <= 768);
  }

  // Start continuous dominance enforcement to override PDF viewer manipulations
  private startDominanceEnforcement(): void {
    if (this.dominanceInterval) {
      clearInterval(this.dominanceInterval);
    }

    this.dominanceInterval = window.setInterval(() => {
      this.enforceDominance();
    }, 100); // Check every 100ms
  }

  // Enforce our control over PDF viewer manipulations
  private enforceDominance(): void {
    if (!this.pdfContainerRef?.nativeElement || !this.canvases.length) return;

    // Ensure fabric canvas wrappers stay interactive and properly styled
    this.canvases.forEach((fabricCanvas) => {
      if (!fabricCanvas || !fabricCanvas.wrapperEl) return;

      // Force fabric wrapper properties
      if (fabricCanvas.wrapperEl.style.pointerEvents !== (this.options.readOnly ? 'none' : 'all')) {
        fabricCanvas.wrapperEl.style.pointerEvents = this.options.readOnly ? 'none' : 'all';
      }
      if (fabricCanvas.wrapperEl.style.zIndex !== '10001') {
        fabricCanvas.wrapperEl.style.zIndex = '10001';
      }
    });

    // Override any PDF viewer event listeners that might interfere
    this.interceptPdfViewerEvents();
  }

  // Intercept and override PDF viewer event manipulations
  private interceptPdfViewerEvents(): void {
    if (!this.pdfContainerRef?.nativeElement) return;

    const pdfContainer = this.pdfContainerRef.nativeElement;

    // Find PDF canvas elements and ensure they don't interfere with fabric.js
    const pdfCanvases = pdfContainer.querySelectorAll('canvas');

    pdfCanvases.forEach((canvas: Element) => {
      const canvasElement = canvas as HTMLCanvasElement;

      // Check if this is a PDF canvas with fabric.js on it
      const hasFabricWrapper = canvasElement.parentElement?.classList.contains('canvas-container');

      if (hasFabricWrapper) {
        // This is a PDF canvas with fabric.js - ensure fabric wrapper stays interactive
        const wrapper = canvasElement.parentElement as HTMLElement;
        if (wrapper) {
          wrapper.style.pointerEvents = this.options.readOnly ? 'none' : 'all';
          wrapper.style.zIndex = '10001';
        }
      }
    });
  }

  // Stop dominance enforcement
  page: number = 1;
  rotation: number = 0;
  zoom: number = 1.0;

  // Public methods for external control
  zoomIn(step: number = 0.25): void {
    const newZoom = Math.min(this.zoom + step, 3.0); // Max zoom 3x
    this.setZoom(newZoom);
  }

  zoomOut(step: number = 0.25): void {
    const newZoom = Math.max(this.zoom - step, 0.25); // Min zoom 0.25x
    this.setZoom(newZoom);
  }

  setZoom(zoom: number): void {
    if (zoom >= 0.25 && zoom <= 3.0) {
      const oldZoom = this.zoom;
      this.zoom = zoom;
      this.zoomChange.emit(this.zoom);

      // When zoom changes, PDF viewer will update and trigger onPageRendered events
      // which will handle individual page canvas updates
      if (this.elements.length > 0 && oldZoom !== zoom) {
        console.log(`🔍 Zoom changed from ${oldZoom} to ${zoom} - will be handled by PDF viewer update`);
      }
    }
  }

  rotateClockwise(): void {
    this.setRotation((this.rotation + 90) % 360);
  }

  rotateCounterClockwise(): void {
    this.setRotation((this.rotation - 90 + 360) % 360);
  }

  setRotation(rotation: number): void {
    const oldRotation = this.rotation;
    this.rotation = rotation;
    this.rotationChange.emit(this.rotation);

    // Rotation changes will be handled by page re-rendering events
    if (this.elements.length > 0 && oldRotation !== rotation) {
      console.log(`🔄 Rotation changed from ${oldRotation}° to ${rotation}° - will be handled by page re-rendering`);
    }
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.pageCount) {
      this.page = page;
      this.pageChange.emit(this.page);
    }
  }

  nextPage(): void {
    this.setPage(Math.min(this.page + 1, this.pageCount));
  }

  previousPage(): void {
    this.setPage(Math.max(this.page - 1, 1));
  }

  // Reset to default view
  resetView(): void {
    this.setZoom(1.0);
    this.setRotation(0);
    this.setPage(1);
  }

  // Signer Management Methods (Public API)

  /**
   * Add a new signer with auto-assigned color
   * @param name - Display name of the signer
   * @param email - Email address of the signer
   * @param id - Optional unique ID (generated if not provided)
   * @returns The created signer or null if max signers reached
   */
  addSigner(name: string, email: string, id?: string): Signer | null {
    if (this.signers.length >= this.MAX_SIGNERS) {
      console.warn(`Cannot add signer: Maximum of ${this.MAX_SIGNERS} signers allowed`);
      return null;
    }

    const newSigner: Signer = {
      id: id || `signer-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: name.trim(),
      email: email.trim(),
      color: this.assignNextColor()
    };

    this.signers.push(newSigner);

    // Auto-activate first signer
    if (this.signers.length === 1) {
      this.activeSignerId = newSigner.id;
    }

    console.log(`✅ Added signer: ${newSigner.name} (${newSigner.email}) with color ${newSigner.color}`);

    // Check if there's a pending configuration waiting for this signer
    this.checkAndCompletePendingConfiguration();

    // Emit configuration change to update external listeners
    this.emitConfigurationChange();

    return newSigner;
  }

  /**
   * Remove a signer and all their form elements
   * @param signerId - ID of the signer to remove
   * @returns True if signer was removed, false if not found
   */
  removeSigner(signerId: string): boolean {
    const signerIndex = this.signers.findIndex(s => s.id === signerId);
    if (signerIndex === -1) {
      console.warn(`Cannot remove signer: Signer with ID ${signerId} not found`);
      return false;
    }

    const signer = this.signers[signerIndex];

    // Remove all elements belonging to this signer (cascade delete)
    const elementsToRemove = this.elements.filter(el => el.signerId === signerId);
    elementsToRemove.forEach(element => {
      this.removeElementById(element.id);
    });

    // Remove signer
    this.signers.splice(signerIndex, 1);

    // Handle active signer transition
    if (this.activeSignerId === signerId) {
      this.activeSignerId = this.signers.length > 0 ? this.signers[0].id : null;
    }

    console.log(`✅ Removed signer: ${signer.name} and ${elementsToRemove.length} elements`);

    // Emit configuration change to update external listeners
    this.emitConfigurationChange();

    return true;
  }

  /**
   * Activate a signer (only one can be active at a time)
   * @param signerId - ID of the signer to activate
   * @returns True if signer was activated, false if not found
   */
  async activateSigner(signerId: string): Promise<boolean> {
    const signer = this.signers.find(s => s.id === signerId);
    if (!signer) {
      console.warn(`Cannot activate signer: Signer with ID ${signerId} not found`);
      return false;
    }

    const oldActiveId = this.activeSignerId;
    this.activeSignerId = signerId;

    if (oldActiveId !== signerId) {
      console.log(`✅ Activated signer: ${signer.name} (${signer.email})`);
      // Refresh element styling to update active/inactive states
      this.refreshElementSignerStates();
    }

    // Emit configuration change
    this.emitConfigurationChange();

    return true;
  }

  /**
   * Get all signers
   * @returns Array of all signers
   */
  getSigners(): Signer[] {
    return [...this.signers];
  }

  /**
   * Get the currently active signer
   * @returns Active signer or null if none active
   */
  getActiveSigner(): Signer | null {
    if (!this.activeSignerId) return null;
    return this.signers.find(s => s.id === this.activeSignerId) || null;
  }

  /**
   * Get signer by ID
   * @param signerId - ID of the signer to find
   * @returns Signer or null if not found
   */
  getSignerById(signerId: string): Signer | null {
    return this.signers.find(s => s.id === signerId) || null;
  }

  /**
   * Assign next available pastel color
   * @returns Hex color string
   */
  private assignNextColor(): string {
    // Find next unused color
    while (this.usedColorIndices.has(this.nextColorIndex)) {
      this.nextColorIndex = (this.nextColorIndex + 1) % PASTEL_COLORS.length;
    }

    const color = PASTEL_COLORS[this.nextColorIndex];
    this.usedColorIndices.add(this.nextColorIndex);

    // Move to next color for next assignment
    this.nextColorIndex = (this.nextColorIndex + 1) % PASTEL_COLORS.length;

    return color;
  }

  /**
   * Check if pending configuration can be completed with current signers
   * Automatically loads the configuration when all required signers are available
   */
  private async checkAndCompletePendingConfiguration(): Promise<void> {
    if (!this.pendingConfiguration) {
      return; // No pending configuration
    }

    console.log('🔍 Checking if pending configuration can be completed...');

    // Update pending configuration with current signers for validation
    const updatedConfig = {
      ...this.pendingConfiguration,
      signers: this.signers,
      activeSignerId: this.pendingConfiguration.activeSignerId || (this.signers.length > 0 ? this.signers[0].id : '')
    };

    // Validate if all required signers are now available
    const validation = this.validateConfiguration(updatedConfig, false);

    if (validation.valid && !validation.missingSigners) {
      console.log('✅ All required signers are now available. Completing configuration load...');
      this.pendingConfiguration = null; // Clear pending before loading to avoid recursion

      // Load the configuration with current signers
      await this.loadConfiguration(updatedConfig, false);
      console.log('🎉 Pending configuration loaded successfully!');
    } else if (validation.missingSigners && validation.missingSigners.length > 0) {
      console.log(`⏳ Still waiting for ${validation.missingSigners.length} signers: ${validation.missingSigners.join(', ')}`);
    }
  }


  private stopDominanceEnforcement(): void {
    if (this.dominanceInterval) {
      clearInterval(this.dominanceInterval);
      this.dominanceInterval = undefined;
    }
  }

  private cleanup(): void {
    // Stop dominance enforcement
    this.stopDominanceEnforcement();

    this.canvases.forEach(canvas => {
      if (canvas) {
        canvas.dispose();
      }
    });

    this.canvases = [];

    // Clean up signer tooltip
    const tooltip = document.getElementById('signer-tooltip');
    if (tooltip) {
      tooltip.remove();
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = undefined;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }

    // Reset initialization flags
    this.isInitialized = false;
    this.eventListenersAdded = false;

    // Clean up mobile gestures
    this.mobileGestureService.cleanup();
  }

  // Modal methods for element property editing
  openEditModal(element: FormElement): void {
    this.editingElement = element;
    this.editForm = {
      name: element.name || '',
      isReadOnly: element.isReadOnly || false,
      properties: { ...element.properties }
    };

    // Set defaults for text element toolbar properties
    if (element.type === FormElementType.TEXT) {
      this.editForm.properties.fontFamily = this.editForm.properties.fontFamily || 'Arial';
      this.editForm.properties.textAlign = this.editForm.properties.textAlign || 'left';
      this.editForm.properties.textColor = this.editForm.properties.textColor || '#000000';
      this.editForm.properties.fontSize = this.editForm.properties.fontSize || 14;
    }

    // Calculate distances for initials
    if (element.type === FormElementType.INITIALS && element.pageDimensions) {
      const pageWidth = element.pageDimensions.width;

      // Distance from right = pageWidth - (x + width)
      this.editForm.distanceFromRight = Math.round(pageWidth - (element.pdfPosition.x + element.pdfPosition.width));

      // Distance from bottom = y (since PDF Y=0 is at bottom)
      this.editForm.distanceFromBottom = Math.round(element.pdfPosition.y);
    }

    this.isEditModalVisible = true;
  }

  closeEditModal(): void {
    this.isEditModalVisible = false;
    this.editingElement = null;
    this.editForm = {};
    this.editModalError = null; // Clear error on close
    this.showColorPalette = false; // Close color palette
  }

  toggleColorPalette(): void {
    this.showColorPalette = !this.showColorPalette;
  }

  selectColor(color: string): void {
    if (this.editForm.properties) {
      this.editForm.properties.textColor = color;
    }
    this.showColorPalette = false;
  }

  /**
   * Validate element name to ensure global uniqueness
   * No two elements can have the same name, regardless of type or signer
   * EXCEPTION: Radio buttons can share the same name (that's how groups work)
   * BUT: Radio buttons from different signers CANNOT share the same group name
   * @param name The element name to validate
   * @param currentElementId The ID of the element being edited (to exclude from duplicate check)
   * @param currentElementType The type of the element being validated
   * @param currentSignerId The signer ID of the element being validated
   * @returns Validation result with error message if invalid
   */
  private validateElementName(name: string, currentElementId: string, currentElementType: FormElementType, currentSignerId: string): { valid: boolean, error: string } {
    // Check for empty name
    if (!name || name.trim() === '') {
      return {
        valid: false,
        error: this.t('errors.elementNameEmpty')
      };
    }

    // Find elements with the same name (excluding current element)
    const elementsWithSameName = this.elements.filter(
      el => el.name === name.trim() && el.id !== currentElementId
    );

    if (elementsWithSameName.length === 0) {
      return { valid: true, error: '' }; // No conflict
    }

    // Special handling for radio buttons: they CAN share names within the same signer
    if (currentElementType === FormElementType.RADIO_BUTTON) {
      // Radio buttons can share names with other radio buttons from THE SAME SIGNER (same group)
      // But cannot share names with non-radio elements OR radio buttons from DIFFERENT SIGNERS

      // Check for non-radio conflicts
      const hasNonRadioConflict = elementsWithSameName.some(
        el => el.type !== FormElementType.RADIO_BUTTON
      );

      if (hasNonRadioConflict) {
        return {
          valid: false,
          error: this.t('errors.elementNameDuplicate', { name: name.trim() })
        };
      }

      // Check for radio button conflicts from different signers
      const hasDifferentSignerRadioConflict = elementsWithSameName.some(
        el => el.type === FormElementType.RADIO_BUTTON && el.signerId !== currentSignerId
      );

      if (hasDifferentSignerRadioConflict) {
        return {
          valid: false,
          error: this.t('errors.radioGroupNameConflict', { name: name.trim() })
        };
      }

      return { valid: true, error: '' }; // Multiple radio buttons with same name from same signer is OK
    }

    // For non-radio elements: cannot share name with ANY other element
    const hasRadioConflict = elementsWithSameName.some(
      el => el.type === FormElementType.RADIO_BUTTON
    );

    if (hasRadioConflict) {
      return {
        valid: false,
        error: this.t('errors.elementNameDuplicate', { name: name.trim() })
      };
    }

    // Regular duplicate check for non-radio elements
    return {
      valid: false,
      error: this.t('errors.elementNameDuplicate', { name: name.trim() })
    };
  }

  /**
   * Validate radio button option value to prevent duplicates and empty values
   * @param groupName The radio button group name (element.name field)
   * @param selectedValue The option value to validate
   * @param currentElementId The ID of the element being edited (to exclude from duplicate check)
   * @returns Validation result with error message if invalid
   */
  private validateRadioButtonOption(groupName: string, selectedValue: string, currentElementId: string): { valid: boolean, error: string } {
    // Check for empty value
    if (!selectedValue || selectedValue.trim() === '') {
      return {
        valid: false,
        error: this.t('errors.selectedValueEmpty')
      };
    }

    // Find all radio buttons in the same group
    const groupRadioButtons = this.elements.filter(
      el => el.type === FormElementType.RADIO_BUTTON &&
            el.name === groupName &&
            el.id !== currentElementId // Exclude current element
    ) as RadioButtonElement[];

    // Check for duplicate values
    const duplicateExists = groupRadioButtons.some(
      rb => rb.properties.selectedValue === selectedValue.trim()
    );

    if (duplicateExists) {
      return {
        valid: false,
        error: this.t('errors.selectedValueDuplicate', {
          value: selectedValue.trim(),
          group: groupName
        })
      };
    }

    return { valid: true, error: '' };
  }

  /**
   * Validate attachments list element properties
   * Ensures all numeric values are within acceptable ranges
   */
  private validateAttachmentsListProperties(): boolean {
    if (this.editingElement?.type !== FormElementType.ATTACHMENTS_LIST) {
      return true;
    }

    const props = this.editForm.properties;

    // Validate font size range (6-18)
    if (props.fontSize !== undefined && (props.fontSize < 6 || props.fontSize > 18)) {
      this.editModalError = this.t('errors.fontSizeRange', { min: 6, max: 18 }) || 'Font size must be between 6 and 18 points';
      return false;
    }

    return true;
  }

  saveElementProperties(): void {
    if (!this.editingElement) return;

    // Clear any previous errors
    this.editModalError = null;

    // Validate element name for ALL element types (global uniqueness check)
    // Skip for signature types that have disabled name fields
    if (this.editingElement.type !== FormElementType.SIGNATURE &&
        this.editingElement.type !== FormElementType.DIGITAL_SIGNATURE &&
        this.editingElement.type !== FormElementType.BIOMETRIC_SIGNATURE &&
        this.editingElement.type !== FormElementType.INITIALS) {
      const nameValidation = this.validateElementName(
        this.editForm.name,
        this.editingElement.id,
        this.editingElement.type,
        this.editingElement.signerId
      );

      if (!nameValidation.valid) {
        this.editModalError = nameValidation.error;
        console.error(`❌ Element name validation failed: ${nameValidation.error}`);
        return; // Prevent save
      }
    }

    // Additional validation for radio button option values
    if (this.editingElement.type === FormElementType.RADIO_BUTTON) {
      const groupName = this.editForm.name;
      const selectedValue = this.editForm.properties.selectedValue;
      const validation = this.validateRadioButtonOption(groupName, selectedValue, this.editingElement.id);

      if (!validation.valid) {
        this.editModalError = validation.error;
        console.error(`❌ Radio button validation failed: ${validation.error}`);
        return; // Prevent save
      }
    }

    // Additional validation for attachments list properties
    if (!this.validateAttachmentsListProperties()) {
      console.error(`❌ Attachments list validation failed: ${this.editModalError}`);
      return; // Prevent save
    }

    // Special handling for initials - update position based on distances
    if (this.editingElement.type === FormElementType.INITIALS && this.editingElement.pageDimensions) {
      const pageWidth = this.editingElement.pageDimensions.width;
      const width = this.editingElement.pdfPosition.width;

      // Calculate new x position from distance from right
      const newX = pageWidth - this.editForm.distanceFromRight - width;
      // Y position is the distance from bottom
      const newY = this.editForm.distanceFromBottom;

      console.log(`📍 Updating initials position: x=${newX}, y=${newY} (from distances: right=${this.editForm.distanceFromRight}, bottom=${this.editForm.distanceFromBottom})`);

      // Update PDF position
      this.editingElement.pdfPosition.x = Math.max(0, newX);
      this.editingElement.pdfPosition.y = Math.max(0, newY);

      // Also update legacy position for compatibility
      if (this.editingElement.position) {
        this.editingElement.position.x = this.editingElement.pdfPosition.x;
        this.editingElement.position.y = this.editingElement.pdfPosition.y;
      }

      // Clear screen position to force recalculation
      this.editingElement.screenPosition = undefined;
    }

    // Update the element with new properties
    this.editingElement.name = this.editForm.name;
    this.editingElement.isReadOnly = this.editForm.isReadOnly;
    this.editingElement.properties = { ...this.editForm.properties };

    // Find the fabric object and update it
    const canvas = this.canvases[this.editingElement.pageIndex];
    if (canvas) {
      const fabricObject = canvas.getObjects().find(obj => (obj as any).id === this.editingElement!.id);
      if (fabricObject) {
        // Recreate the fabric object with new properties using fabric canvas
        const canvas = this.canvases[this.editingElement.pageIndex];
        if (!canvas) {
          console.error(`Cannot update element properties: Fabric canvas not found for page ${this.editingElement.pageIndex + 1}`);
          return;
        }

        const fabricCanvas = canvas.getElement();
        const viewport = this.coordinateTransform.createViewportFromCanvas(
          fabricCanvas,
          this.zoom,
          this.rotation
        );

        // Remove old object
        canvas.remove(fabricObject);

        // Create new object with updated properties
        // Extract fresh page dimensions from current PDF
        const pageWrapper = this.pdfContainerRef.nativeElement.querySelector(`[data-page-number="${this.editingElement.pageIndex + 1}"]`);
        let pageDimensions: { width: number; height: number };
        if (pageWrapper) {
          pageDimensions = this.extractPageDimensions(pageWrapper);
          this.editingElement.pageDimensions = pageDimensions; // Update with current PDF dimensions
        } else if (this.editingElement.pageDimensions) {
          pageDimensions = this.editingElement.pageDimensions;
          console.warn(`⚠️ Could not find page wrapper for page ${this.editingElement.pageIndex + 1}, using stored dimensions`);
        } else {
          console.error(`❌ Element ${this.editingElement.id} missing pageDimensions and cannot extract from PDF`);
          return;
        }

        // Get signer information for styling
        const signer = this.getSignerById(this.editingElement.signerId);
        // Recalculate screen position for initials with updated PDF coordinates
        if (this.editingElement.type === FormElementType.INITIALS) {
          this.editingElement.screenPosition = this.coordinateTransform.pdfRectToScreen(
            this.editingElement.pdfPosition,
            viewport,
            pageDimensions
          );
          console.log(`📐 Recalculated screen position:`, this.editingElement.screenPosition);
        }

        const newFabricObject = FormElementFactory.createFabricObject(this.editingElement, viewport, pageDimensions, this.translations);

        // Apply signer color as background for signature elements
        if (signer?.color && this.isSignatureElement(this.editingElement)) {
          this.applySignerColorToSignature(newFabricObject, signer.color);
        }

        this.addCustomControls(newFabricObject, this.editingElement);

        canvas.add(newFabricObject);
        canvas.setActiveObject(newFabricObject);
        canvas.renderAll();

        console.log(`✏️ Updated element properties for ${this.editingElement.type}:`, {
          pdfPosition: this.editingElement.pdfPosition,
          screenPosition: this.editingElement.screenPosition,
          properties: this.editingElement.properties
        });
      }
    }

    // Emit events
    this.elementModified.emit(this.editingElement);
    this.emitConfigurationChange();

    // Close modal
    this.closeEditModal();
  }

  deleteElement(element: FormElement): void {
    if (this.options.readOnly) return;
    // AN-1801 — signing placement keeps exactly one non-deletable box.
    if (this.options.signingPlacement) return;

    const canvas = this.canvases[element.pageIndex];
    if (canvas) {
      const fabricObject = canvas.getObjects().find(obj => (obj as any).id === element.id);
      if (fabricObject) {
        canvas.remove(fabricObject);
        canvas.renderAll();
      }
    }

    // Remove from elements array
    this.removeElementFromArray(element);

    // Update selection
    if (this.selectedElement === element) {
      this.selectedElement = null;
      this.elementSelected.emit(null);
    }

    this.elementRemoved.emit(element);
    this.emitConfigurationChange();

    console.log(`🗑️ Deleted element ${element.type} with id ${element.id}`);
  }

  // Add initials to all other pages at same distance from right/bottom
  async addInitialsToOtherPages(): Promise<void> {
    if (!this.editingElement || this.editingElement.type !== FormElementType.INITIALS) return;

    const distanceFromRight = this.editForm.distanceFromRight || 0;
    const distanceFromBottom = this.editForm.distanceFromBottom || 0;
    const currentPageIndex = this.editingElement.pageIndex;
    const signerId = this.editingElement.signerId;

    // Try to get actual page count by counting PDF canvases
    let actualPageCount = this.pageCount;
    if (actualPageCount <= 1) {
      const pdfContainer = this.pdfContainerRef?.nativeElement;
      if (pdfContainer) {
        const allPdfCanvases = pdfContainer.querySelectorAll('canvas.ng2-pdf-viewer-canvas, canvas[class*="pdf"], .page canvas');
        if (allPdfCanvases.length > 1) {
          actualPageCount = allPdfCanvases.length;
          this.pageCount = actualPageCount;
          console.log(`📄 Updated page count by counting canvases: ${this.pageCount}`);
        }
      }
    }

    console.log(`📄 Adding initials to other pages. Distance from right: ${distanceFromRight}, Distance from bottom: ${distanceFromBottom}`);
    console.log(`Current page: ${currentPageIndex + 1}, Total pages: ${actualPageCount}`);

    let addedCount = 0;

    // Loop through all pages except the current one
    for (let pageIndex = 0; pageIndex < actualPageCount; pageIndex++) {
      if (pageIndex === currentPageIndex) continue;

      // Check if initials already exist on this page for this signer
      const existingInitials = this.elements.find(el =>
        el.type === FormElementType.INITIALS &&
        el.pageIndex === pageIndex &&
        el.signerId === signerId
      );

      if (!existingInitials) {
        // Extract actual page dimensions for this target page
        const targetPageWrapper = this.pdfContainerRef.nativeElement.querySelector(`[data-page-number="${pageIndex + 1}"]`);
        if (!targetPageWrapper) {
          console.warn(`⚠️ Could not find page wrapper for page ${pageIndex + 1}, skipping`);
          continue;
        }
        const pageDimensions = this.extractPageDimensions(targetPageWrapper);

        // Keep same absolute size as the source element
        const width = this.editingElement.pdfPosition.width;
        const height = this.editingElement.pdfPosition.height;

        // Calculate position using TARGET page dimensions
        const x = Math.max(0, pageDimensions.width - distanceFromRight - width);
        const y = Math.max(0, distanceFromBottom);

        console.log(`Creating initials at position x=${x}, y=${y} on page ${pageIndex + 1}`);

        // Create new initials element with proper PDF position
        const newInitials = FormElementFactory.createFormElement(
          FormElementType.INITIALS,
          { x, y, width, height },
          pageIndex,
          signerId,
          { ...this.editingElement.properties },
          this.elements
        ) as any;

        // Set page dimensions
        newInitials.pageDimensions = pageDimensions;

        // Ensure pdfPosition is properly set
        newInitials.pdfPosition = { x, y, width, height };

        // Also set legacy position for compatibility
        newInitials.position = { x, y, width, height };

        // AN-1851 — register a floor for completeness. Initials are unconstrained, so this
        // resolves to no floor; it is done anyway so that every element in `elements` has an
        // entry and an unregistered element can never be mistaken for an unconstrained one.
        this.registerSizeFloor(newInitials, true);

        // Add to elements array
        this.elements.push(newInitials);
        addedCount++;
        console.log(`✅ Added initials element to page ${pageIndex + 1}`);

        // Find the PDF canvas for this page to render the element
        const pdfCanvas = this.findCanvasByPageNumber(pageIndex + 1);
        if (pdfCanvas) {
          // If canvas doesn't exist for that page, create it
          if (!this.canvases[pageIndex]) {
            await this.createCanvasForPage(pdfCanvas);
            console.log(`🎨 Created canvas overlay for page ${pageIndex + 1}`);
          }

          // Now add the fabric object to the canvas
          const targetCanvas = this.canvases[pageIndex];
          if (targetCanvas) {
            const fabricCanvas = targetCanvas.getElement();
            const viewport = this.coordinateTransform.createViewportFromCanvas(
              fabricCanvas,
              this.zoom,
              this.rotation
            );

            // Calculate screen position for the new element
            newInitials.screenPosition = this.coordinateTransform.pdfRectToScreen(
              newInitials.pdfPosition,
              viewport,
              pageDimensions
            );

            const fabricObject = FormElementFactory.createFabricObject(newInitials, viewport, pageDimensions, this.translations);
            this.addCustomControls(fabricObject, newInitials);

            // Apply signer-based interactivity
            const signer = this.getSignerById(signerId);
            if (signer) {
              const isActive = signerId === this.activeSignerId;
              fabricObject.set({
                selectable: isActive && !this.options.readOnly,
                evented: isActive && !this.options.readOnly,
                opacity: isActive ? 1.0 : 0.6,
                borderColor: signer.color,
                cornerColor: signer.color,
                cornerStrokeColor: signer.color,
                borderScaleFactor: 2,
                hoverCursor: isActive && !this.options.readOnly ? 'move' : 'default'
              });

              // Apply signer color to initials background if it's a signature-type element
              if (this.isSignatureElement(newInitials)) {
                this.applySignerColorToSignature(fabricObject, signer.color);
              }
            }

            targetCanvas.add(fabricObject);
            targetCanvas.renderAll();
            console.log(`✅ Rendered initials on canvas for page ${pageIndex + 1}`);
          } else {
            console.warn(`⚠️ Could not get canvas for page ${pageIndex + 1}`);
          }
        } else {
          // Page not yet rendered (lazy loading) - element is already in this.elements[]
          // It will be rendered automatically when user scrolls to this page via onPageRendered()
          console.log(`📋 Page ${pageIndex + 1} not yet visible, element will render when scrolled into view`);
        }
      } else {
        console.log(`⚠️ Initials already exist on page ${pageIndex + 1} for this signer`);
      }
    }

    console.log(`✅ Successfully added initials to ${addedCount} pages`);

    // Emit configuration change to update the demo
    this.emitConfigurationChange();

    // Force re-render of all pages by triggering a zoom change
    // This ensures elements on cached pages get rendered
    if (addedCount > 0) {
      const originalZoom = this.zoom;
      this.zoom = originalZoom + 0.001;
      setTimeout(() => {
        this.zoom = originalZoom;
        console.log('🔄 Forced page re-render to display new elements');
      }, 100);
    }

    // Close the modal
    this.closeEditModal();
  }

  // Delete all initials for the current signer
  deleteAllInitialsForSigner(): void {
    if (!this.editingElement || this.editingElement.type !== FormElementType.INITIALS) return;

    const signerId = this.editingElement.signerId;
    console.log(`🗑️ Deleting all initials for signer: ${signerId}`);

    // Find all initials elements for this signer
    const initialsToDelete = this.elements.filter(el =>
      el.type === FormElementType.INITIALS &&
      el.signerId === signerId
    );

    console.log(`Found ${initialsToDelete.length} initials to delete`);

    // Delete each initials element
    initialsToDelete.forEach(element => {
      // Remove from canvas
      const canvas = this.canvases[element.pageIndex];
      if (canvas) {
        const fabricObject = canvas.getObjects().find(obj => (obj as any).id === element.id);
        if (fabricObject) {
          canvas.remove(fabricObject);
          canvas.renderAll();
          console.log(`✅ Removed initials from canvas on page ${element.pageIndex + 1}`);
        }
      }
    });

    // Remove all initials from elements array at once
    this.elements = this.elements.filter(el =>
      !(el.type === FormElementType.INITIALS && el.signerId === signerId)
    );

    // Clear selection if we deleted the selected element
    if (this.selectedElement && initialsToDelete.includes(this.selectedElement)) {
      this.selectedElement = null;
      this.elementSelected.emit(null);
    }

    console.log(`✅ Deleted ${initialsToDelete.length} initials elements`);

    // Emit events
    this.emitConfigurationChange();
    this.closeEditModal();
  }

  onError($event: any) {

  }

  onPagesInitialized($event: CustomEvent<any>) {
    console.log('onPagesInitialized with pages:', $event);
    // Update page count from pages initialized event
    if ($event && $event.detail && $event.detail.pagesCount) {
      this.pageCount = $event.detail.pagesCount;
      console.log(`📄 Updated page count from onPagesInitialized: ${this.pageCount}`);
    } else if ($event && typeof $event.detail === 'number') {
      this.pageCount = $event.detail;
      console.log(`📄 Updated page count from onPagesInitialized (direct): ${this.pageCount}`);
    }
  }

  onPDFChange($event: Event) {
    console.log('PDF changed:', $event);
  }
}
